import { css as I, LitElement as D } from "lit";
import { createContext as De, provide as Te, consume as Le } from "@lit/context";
import { PasskeyError as F, PasskeyStepName as u } from "@caas-xyz/core";
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const _ = (t) => (e, s) => {
  s !== void 0 ? s.addInitializer((() => {
    customElements.define(t, e);
  })) : customElements.define(t, e);
};
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Z = globalThis, ne = Z.ShadowRoot && (Z.ShadyCSS === void 0 || Z.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Se = Symbol(), he = /* @__PURE__ */ new WeakMap();
let Oe = class {
  constructor(e, s, a) {
    if (this._$cssResult$ = !0, a !== Se) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = s;
  }
  get styleSheet() {
    let e = this.o;
    const s = this.t;
    if (ne && e === void 0) {
      const a = s !== void 0 && s.length === 1;
      a && (e = he.get(s)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), a && he.set(s, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const ze = (t) => new Oe(typeof t == "string" ? t : t + "", void 0, Se), Me = (t, e) => {
  if (ne) t.adoptedStyleSheets = e.map(((s) => s instanceof CSSStyleSheet ? s : s.styleSheet));
  else for (const s of e) {
    const a = document.createElement("style"), i = Z.litNonce;
    i !== void 0 && a.setAttribute("nonce", i), a.textContent = s.cssText, t.appendChild(a);
  }
}, ue = ne ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let s = "";
  for (const a of e.cssRules) s += a.cssText;
  return ze(s);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Re, defineProperty: je, getOwnPropertyDescriptor: Ne, getOwnPropertyNames: Ue, getOwnPropertySymbols: Be, getPrototypeOf: Fe } = Object, se = globalThis, ge = se.trustedTypes, He = ge ? ge.emptyScript : "", Ve = se.reactiveElementPolyfillSupport, q = (t, e) => t, Q = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? He : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let s = t;
  switch (e) {
    case Boolean:
      s = t !== null;
      break;
    case Number:
      s = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        s = JSON.parse(t);
      } catch {
        s = null;
      }
  }
  return s;
} }, ce = (t, e) => !Re(t, e), fe = { attribute: !0, type: String, converter: Q, reflect: !1, useDefault: !1, hasChanged: ce };
Symbol.metadata ??= Symbol("metadata"), se.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
let H = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ??= []).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, s = fe) {
    if (s.state && (s.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(e) && ((s = Object.create(s)).wrapped = !0), this.elementProperties.set(e, s), !s.noAccessor) {
      const a = Symbol(), i = this.getPropertyDescriptor(e, a, s);
      i !== void 0 && je(this.prototype, e, i);
    }
  }
  static getPropertyDescriptor(e, s, a) {
    const { get: i, set: r } = Ne(this.prototype, e) ?? { get() {
      return this[s];
    }, set(o) {
      this[s] = o;
    } };
    return { get: i, set(o) {
      const d = i?.call(this);
      r?.call(this, o), this.requestUpdate(e, d, a);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? fe;
  }
  static _$Ei() {
    if (this.hasOwnProperty(q("elementProperties"))) return;
    const e = Fe(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(q("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(q("properties"))) {
      const s = this.properties, a = [...Ue(s), ...Be(s)];
      for (const i of a) this.createProperty(i, s[i]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const s = litPropertyMetadata.get(e);
      if (s !== void 0) for (const [a, i] of s) this.elementProperties.set(a, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [s, a] of this.elementProperties) {
      const i = this._$Eu(s, a);
      i !== void 0 && this._$Eh.set(i, s);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const s = [];
    if (Array.isArray(e)) {
      const a = new Set(e.flat(1 / 0).reverse());
      for (const i of a) s.unshift(ue(i));
    } else e !== void 0 && s.push(ue(e));
    return s;
  }
  static _$Eu(e, s) {
    const a = s.attribute;
    return a === !1 ? void 0 : typeof a == "string" ? a : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise(((e) => this.enableUpdating = e)), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach(((e) => e(this)));
  }
  addController(e) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(e), this.renderRoot !== void 0 && this.isConnected && e.hostConnected?.();
  }
  removeController(e) {
    this._$EO?.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), s = this.constructor.elementProperties;
    for (const a of s.keys()) this.hasOwnProperty(a) && (e.set(a, this[a]), delete this[a]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Me(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach(((e) => e.hostConnected?.()));
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    this._$EO?.forEach(((e) => e.hostDisconnected?.()));
  }
  attributeChangedCallback(e, s, a) {
    this._$AK(e, a);
  }
  _$ET(e, s) {
    const a = this.constructor.elementProperties.get(e), i = this.constructor._$Eu(e, a);
    if (i !== void 0 && a.reflect === !0) {
      const r = (a.converter?.toAttribute !== void 0 ? a.converter : Q).toAttribute(s, a.type);
      this._$Em = e, r == null ? this.removeAttribute(i) : this.setAttribute(i, r), this._$Em = null;
    }
  }
  _$AK(e, s) {
    const a = this.constructor, i = a._$Eh.get(e);
    if (i !== void 0 && this._$Em !== i) {
      const r = a.getPropertyOptions(i), o = typeof r.converter == "function" ? { fromAttribute: r.converter } : r.converter?.fromAttribute !== void 0 ? r.converter : Q;
      this._$Em = i;
      const d = o.fromAttribute(s, r.type);
      this[i] = d ?? this._$Ej?.get(i) ?? d, this._$Em = null;
    }
  }
  requestUpdate(e, s, a) {
    if (e !== void 0) {
      const i = this.constructor, r = this[e];
      if (a ??= i.getPropertyOptions(e), !((a.hasChanged ?? ce)(r, s) || a.useDefault && a.reflect && r === this._$Ej?.get(e) && !this.hasAttribute(i._$Eu(e, a)))) return;
      this.C(e, s, a);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(e, s, { useDefault: a, reflect: i, wrapped: r }, o) {
    a && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(e) && (this._$Ej.set(e, o ?? s ?? this[e]), r !== !0 || o !== void 0) || (this._$AL.has(e) || (this.hasUpdated || a || (s = void 0), this._$AL.set(e, s)), i === !0 && this._$Em !== e && (this._$Eq ??= /* @__PURE__ */ new Set()).add(e));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (s) {
      Promise.reject(s);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [i, r] of this._$Ep) this[i] = r;
        this._$Ep = void 0;
      }
      const a = this.constructor.elementProperties;
      if (a.size > 0) for (const [i, r] of a) {
        const { wrapped: o } = r, d = this[i];
        o !== !0 || this._$AL.has(i) || d === void 0 || this.C(i, void 0, r, d);
      }
    }
    let e = !1;
    const s = this._$AL;
    try {
      e = this.shouldUpdate(s), e ? (this.willUpdate(s), this._$EO?.forEach(((a) => a.hostUpdate?.())), this.update(s)) : this._$EM();
    } catch (a) {
      throw e = !1, this._$EM(), a;
    }
    e && this._$AE(s);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    this._$EO?.forEach(((s) => s.hostUpdated?.())), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Eq &&= this._$Eq.forEach(((s) => this._$ET(s, this[s]))), this._$EM();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
H.elementStyles = [], H.shadowRootOptions = { mode: "open" }, H[q("elementProperties")] = /* @__PURE__ */ new Map(), H[q("finalized")] = /* @__PURE__ */ new Map(), Ve?.({ ReactiveElement: H }), (se.reactiveElementVersions ??= []).push("2.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const We = { attribute: !0, type: String, converter: Q, reflect: !1, hasChanged: ce }, qe = (t = We, e, s) => {
  const { kind: a, metadata: i } = s;
  let r = globalThis.litPropertyMetadata.get(i);
  if (r === void 0 && globalThis.litPropertyMetadata.set(i, r = /* @__PURE__ */ new Map()), a === "setter" && ((t = Object.create(t)).wrapped = !0), r.set(s.name, t), a === "accessor") {
    const { name: o } = s;
    return { set(d) {
      const l = e.get.call(this);
      e.set.call(this, d), this.requestUpdate(o, l, t);
    }, init(d) {
      return d !== void 0 && this.C(o, void 0, t, d), d;
    } };
  }
  if (a === "setter") {
    const { name: o } = s;
    return function(d) {
      const l = this[o];
      e.call(this, d), this.requestUpdate(o, l, t);
    };
  }
  throw Error("Unsupported decorator location: " + a);
};
function c(t) {
  return (e, s) => typeof s == "object" ? qe(t, e, s) : ((a, i, r) => {
    const o = i.hasOwnProperty(r);
    return i.constructor.createProperty(r, a), o ? Object.getOwnPropertyDescriptor(i, r) : void 0;
  })(t, e, s);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function f(t) {
  return c({ ...t, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const le = globalThis, ee = le.trustedTypes, me = ee ? ee.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, Ce = "$lit$", A = `lit$${Math.random().toFixed(9).slice(2)}$`, $e = "?" + A, Ye = `<${$e}>`, M = document, te = () => M.createComment(""), Y = (t) => t === null || typeof t != "object" && typeof t != "function", de = Array.isArray, Ge = (t) => de(t) || typeof t?.[Symbol.iterator] == "function", oe = `[ 	
\f\r]`, V = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ve = /-->/g, be = />/g, O = RegExp(`>|${oe}(?:([^\\s"'>=/]+)(${oe}*=${oe}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), ye = /'/g, xe = /"/g, _e = /^(?:script|style|textarea|title)$/i, Ke = (t) => (e, ...s) => ({ _$litType$: t, strings: e, values: s }), Xe = Ke(1), G = Symbol.for("lit-noChange"), b = Symbol.for("lit-nothing"), ke = /* @__PURE__ */ new WeakMap(), z = M.createTreeWalker(M, 129);
function Pe(t, e) {
  if (!de(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return me !== void 0 ? me.createHTML(e) : e;
}
const Je = (t, e) => {
  const s = t.length - 1, a = [];
  let i, r = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", o = V;
  for (let d = 0; d < s; d++) {
    const l = t[d];
    let p, v, h = -1, S = 0;
    for (; S < l.length && (o.lastIndex = S, v = o.exec(l), v !== null); ) S = o.lastIndex, o === V ? v[1] === "!--" ? o = ve : v[1] !== void 0 ? o = be : v[2] !== void 0 ? (_e.test(v[2]) && (i = RegExp("</" + v[2], "g")), o = O) : v[3] !== void 0 && (o = O) : o === O ? v[0] === ">" ? (o = i ?? V, h = -1) : v[1] === void 0 ? h = -2 : (h = o.lastIndex - v[2].length, p = v[1], o = v[3] === void 0 ? O : v[3] === '"' ? xe : ye) : o === xe || o === ye ? o = O : o === ve || o === be ? o = V : (o = O, i = void 0);
    const P = o === O && t[d + 1].startsWith("/>") ? " " : "";
    r += o === V ? l + Ye : h >= 0 ? (a.push(p), l.slice(0, h) + Ce + l.slice(h) + A + P) : l + A + (h === -2 ? d : P);
  }
  return [Pe(t, r + (t[s] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), a];
};
class K {
  constructor({ strings: e, _$litType$: s }, a) {
    let i;
    this.parts = [];
    let r = 0, o = 0;
    const d = e.length - 1, l = this.parts, [p, v] = Je(e, s);
    if (this.el = K.createElement(p, a), z.currentNode = this.el.content, s === 2 || s === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (i = z.nextNode()) !== null && l.length < d; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const h of i.getAttributeNames()) if (h.endsWith(Ce)) {
          const S = v[o++], P = i.getAttribute(h).split(A), J = /([.?@])?(.*)/.exec(S);
          l.push({ type: 1, index: r, name: J[2], strings: P, ctor: J[1] === "." ? Qe : J[1] === "?" ? et : J[1] === "@" ? tt : ie }), i.removeAttribute(h);
        } else h.startsWith(A) && (l.push({ type: 6, index: r }), i.removeAttribute(h));
        if (_e.test(i.tagName)) {
          const h = i.textContent.split(A), S = h.length - 1;
          if (S > 0) {
            i.textContent = ee ? ee.emptyScript : "";
            for (let P = 0; P < S; P++) i.append(h[P], te()), z.nextNode(), l.push({ type: 2, index: ++r });
            i.append(h[S], te());
          }
        }
      } else if (i.nodeType === 8) if (i.data === $e) l.push({ type: 2, index: r });
      else {
        let h = -1;
        for (; (h = i.data.indexOf(A, h + 1)) !== -1; ) l.push({ type: 7, index: r }), h += A.length - 1;
      }
      r++;
    }
  }
  static createElement(e, s) {
    const a = M.createElement("template");
    return a.innerHTML = e, a;
  }
}
function N(t, e, s = t, a) {
  if (e === G) return e;
  let i = a !== void 0 ? s._$Co?.[a] : s._$Cl;
  const r = Y(e) ? void 0 : e._$litDirective$;
  return i?.constructor !== r && (i?._$AO?.(!1), r === void 0 ? i = void 0 : (i = new r(t), i._$AT(t, s, a)), a !== void 0 ? (s._$Co ??= [])[a] = i : s._$Cl = i), i !== void 0 && (e = N(t, i._$AS(t, e.values), i, a)), e;
}
class Ze {
  constructor(e, s) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = s;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: s }, parts: a } = this._$AD, i = (e?.creationScope ?? M).importNode(s, !0);
    z.currentNode = i;
    let r = z.nextNode(), o = 0, d = 0, l = a[0];
    for (; l !== void 0; ) {
      if (o === l.index) {
        let p;
        l.type === 2 ? p = new ae(r, r.nextSibling, this, e) : l.type === 1 ? p = new l.ctor(r, l.name, l.strings, this, e) : l.type === 6 && (p = new st(r, this, e)), this._$AV.push(p), l = a[++d];
      }
      o !== l?.index && (r = z.nextNode(), o++);
    }
    return z.currentNode = M, i;
  }
  p(e) {
    let s = 0;
    for (const a of this._$AV) a !== void 0 && (a.strings !== void 0 ? (a._$AI(e, a, s), s += a.strings.length - 2) : a._$AI(e[s])), s++;
  }
}
class ae {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(e, s, a, i) {
    this.type = 2, this._$AH = b, this._$AN = void 0, this._$AA = e, this._$AB = s, this._$AM = a, this.options = i, this._$Cv = i?.isConnected ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const s = this._$AM;
    return s !== void 0 && e?.nodeType === 11 && (e = s.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, s = this) {
    e = N(this, e, s), Y(e) ? e === b || e == null || e === "" ? (this._$AH !== b && this._$AR(), this._$AH = b) : e !== this._$AH && e !== G && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Ge(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== b && Y(this._$AH) ? this._$AA.nextSibling.data = e : this.T(M.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    const { values: s, _$litType$: a } = e, i = typeof a == "number" ? this._$AC(e) : (a.el === void 0 && (a.el = K.createElement(Pe(a.h, a.h[0]), this.options)), a);
    if (this._$AH?._$AD === i) this._$AH.p(s);
    else {
      const r = new Ze(i, this), o = r.u(this.options);
      r.p(s), this.T(o), this._$AH = r;
    }
  }
  _$AC(e) {
    let s = ke.get(e.strings);
    return s === void 0 && ke.set(e.strings, s = new K(e)), s;
  }
  k(e) {
    de(this._$AH) || (this._$AH = [], this._$AR());
    const s = this._$AH;
    let a, i = 0;
    for (const r of e) i === s.length ? s.push(a = new ae(this.O(te()), this.O(te()), this, this.options)) : a = s[i], a._$AI(r), i++;
    i < s.length && (this._$AR(a && a._$AB.nextSibling, i), s.length = i);
  }
  _$AR(e = this._$AA.nextSibling, s) {
    for (this._$AP?.(!1, !0, s); e !== this._$AB; ) {
      const a = e.nextSibling;
      e.remove(), e = a;
    }
  }
  setConnected(e) {
    this._$AM === void 0 && (this._$Cv = e, this._$AP?.(e));
  }
}
class ie {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, s, a, i, r) {
    this.type = 1, this._$AH = b, this._$AN = void 0, this.element = e, this.name = s, this._$AM = i, this.options = r, a.length > 2 || a[0] !== "" || a[1] !== "" ? (this._$AH = Array(a.length - 1).fill(new String()), this.strings = a) : this._$AH = b;
  }
  _$AI(e, s = this, a, i) {
    const r = this.strings;
    let o = !1;
    if (r === void 0) e = N(this, e, s, 0), o = !Y(e) || e !== this._$AH && e !== G, o && (this._$AH = e);
    else {
      const d = e;
      let l, p;
      for (e = r[0], l = 0; l < r.length - 1; l++) p = N(this, d[a + l], s, l), p === G && (p = this._$AH[l]), o ||= !Y(p) || p !== this._$AH[l], p === b ? e = b : e !== b && (e += (p ?? "") + r[l + 1]), this._$AH[l] = p;
    }
    o && !i && this.j(e);
  }
  j(e) {
    e === b ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class Qe extends ie {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === b ? void 0 : e;
  }
}
class et extends ie {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== b);
  }
}
class tt extends ie {
  constructor(e, s, a, i, r) {
    super(e, s, a, i, r), this.type = 5;
  }
  _$AI(e, s = this) {
    if ((e = N(this, e, s, 0) ?? b) === G) return;
    const a = this._$AH, i = e === b && a !== b || e.capture !== a.capture || e.once !== a.once || e.passive !== a.passive, r = e !== b && (a === b || i);
    i && this.element.removeEventListener(this.name, this, a), r && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class st {
  constructor(e, s, a) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = s, this.options = a;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    N(this, e);
  }
}
const at = le.litHtmlPolyfillSupport;
at?.(K, ae), (le.litHtmlVersions ??= []).push("3.3.1");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const it = Symbol.for(""), rt = (t) => {
  if (t?.r === it) return t?._$litStatic$;
}, we = /* @__PURE__ */ new Map(), ot = (t) => (e, ...s) => {
  const a = s.length;
  let i, r;
  const o = [], d = [];
  let l, p = 0, v = !1;
  for (; p < a; ) {
    for (l = e[p]; p < a && (r = s[p], (i = rt(r)) !== void 0); ) l += i + e[++p], v = !0;
    p !== a && d.push(r), o.push(l), p++;
  }
  if (p === a && o.push(e[a]), v) {
    const h = o.join("$$lit$$");
    (e = we.get(h)) === void 0 && (o.raw = o, we.set(h, e = o)), s = d;
  }
  return t(e, ...s);
}, n = ot(Xe);
var nt = Object.defineProperty, ct = Object.getOwnPropertyDescriptor, B = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? ct(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && nt(e, s, i), i;
};
let C = class extends D {
  /**
   * Constructor - Initialize component with default values
   */
  constructor() {
    super(), this.size = "medium", this.variant = "primary", this.disabled = !1, this.loading = !1;
  }
  /**
   * Renders the button component with appropriate styling and content
   * @returns TemplateResult containing the button HTML
   */
  render() {
    return n`
      <button
        class="btn ${this.size} ${this.variant}"
        ?disabled="${this.disabled || this.loading}"
        @click="${this._handleClick}"
        aria-label="${this.text || ""}"
        aria-busy="${this.loading}"
      >
        ${this.loading ? n`<span class="loading-spinner" aria-hidden="true"></span>` : ""}
        ${this.text ? n`<span>${this.text}</span>` : n`<slot></slot>`}
      </button>
    `;
  }
  /**
   * Handles button click events
   * Validates prerequisites and dispatches custom event
   * @param event - The mouse click event
   * @protected
   */
  _handleClick(t) {
    if (this.disabled || this.loading) {
      console.log("button is disabled or loading"), t.preventDefault(), t.stopPropagation();
      return;
    }
  }
  /**
   * Public method to set the loading state
   * @param loading - Whether the button should be in loading state
   */
  setLoading(t) {
    this.loading = t;
  }
  /**
   * Public method to set the disabled state
   * @param disabled - Whether the button should be disabled
   */
  setDisabled(t) {
    this.disabled = t;
  }
  /**
   * Public method to check if the button is interactive
   * @returns True if the button can be clicked
   */
  isInteractive() {
    return !this.disabled && !this.loading;
  }
};
C.styles = [
  I`
      :host {
        display: inline-block;
      }

      .btn {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--caas-spacing-2);
        font-family: inherit;
        font-weight: var(--caas-font-weight-medium);
        text-decoration: none;
        border: 1px solid transparent;
        border-radius: var(--caas-radius-base);
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        outline: none;
        position: relative;
        overflow: hidden;
        user-select: none;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      }

      /* ===== Primary Variant (Default) ===== */
      .btn.primary {
        background: var(--caas-color-primary);
        color: var(--caas-icon-color);
        border-color: var(--caas-color-primary);
      }

      .btn.primary:hover {
        box-shadow:
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 2px 4px -1px rgba(0, 0, 0, 0.06);
        background: var(--caas-color-primary-hover);
        border-color: var(--caas-color-primary-hover);
      }

      .btn.primary:active {
        background: var(--caas-color-primary-active);
        border-color: var(--caas-color-primary-active);
      }

      .btn.primary:focus-visible {
        box-shadow: 0 0 0 3px var(--caas-color-primary-alpha);
      }

      /* ===== Outline Variant ===== */
      .btn.outline {
        background: transparent;
        color: var(--caas-text-primary);
        border-color: var(--caas-card-border);
      }

      .btn.outline:hover {
        background: var(--caas-button-hover-background);
        border-color: var(--caas-color-primary);
        color: var(--caas-color-primary);
        box-shadow:
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .btn.outline:active {
        background: var(--caas-color-primary-alpha);
        border-color: var(--caas-color-primary);
      }

      .btn.outline:focus-visible {
        box-shadow: 0 0 0 3px var(--caas-color-primary-alpha);
        border-color: var(--caas-color-primary);
      }

      /* ===== Destructive Variant ===== */
      .btn.destructive {
        background: var(--caas-color-error);
        color: var(--caas-icon-color);
        border-color: var(--caas-color-error);
      }

      .btn.destructive:hover {
        background: color-mix(in srgb, var(--caas-color-error) 85%, black);
        border-color: color-mix(in srgb, var(--caas-color-error) 85%, black);
        box-shadow:
          0 4px 6px -1px rgba(0, 0, 0, 0.1),
          0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .btn.destructive:active {
        background: color-mix(in srgb, var(--caas-color-error) 70%, black);
        border-color: color-mix(in srgb, var(--caas-color-error) 70%, black);
      }

      .btn.destructive:focus-visible {
        box-shadow: 0 0 0 3px var(--caas-color-error-alpha);
      }

      .btn:disabled {
        cursor: not-allowed;
        opacity: 0.6;
        pointer-events: none;
      }

      .btn.loading {
        cursor: wait;
        pointer-events: none;
      }

      /* Size variants */
      .btn.small {
        padding: var(--caas-spacing-1) var(--caas-spacing-3);
        font-size: var(--caas-font-size-base);
        line-height: var(--caas-line-height-tight);
        height: 32px;
      }

      .btn.medium {
        padding: var(--caas-spacing-2) var(--caas-spacing-4);
        font-size: var(--caas-font-size-lg);
        line-height: var(--caas-line-height-normal);
        height: 40px;
      }

      .btn.large {
        padding: var(--caas-spacing-3) var(--caas-spacing-6);
        font-size: var(--caas-font-size-xl);
        line-height: var(--caas-line-height-normal);
        height: 48px;
      }

      /* Loading spinner */
      .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid transparent;
        border-top: 2px solid currentColor;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      /* Mobile responsive */
      @media (max-width: 768px) {
        .btn.small {
          min-height: 36px;
          padding: var(--caas-spacing-2) var(--caas-spacing-3);
        }

        .btn.medium {
          min-height: 44px;
          padding: var(--caas-spacing-2) var(--caas-spacing-4);
        }

        .btn.large {
          min-height: 52px;
          padding: var(--caas-spacing-3) var(--caas-spacing-6);
        }
      }

      /* High contrast mode support */
      @media (prefers-contrast: high) {
        .btn {
          border-width: 2px;
        }

        .btn:focus-visible {
          outline: 3px solid;
          outline-offset: 2px;
        }
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .btn {
          transition: none;
        }

        .loading-spinner {
          animation: none;
        }
      }
    `
];
B([
  c({ type: String })
], C.prototype, "text", 2);
B([
  c({ type: String })
], C.prototype, "size", 2);
B([
  c({ type: String })
], C.prototype, "variant", 2);
B([
  c({ type: Boolean, reflect: !0 })
], C.prototype, "disabled", 2);
B([
  c({ type: Boolean, reflect: !0 })
], C.prototype, "loading", 2);
C = B([
  _("caas-button")
], C);
var lt = Object.defineProperty, dt = Object.getOwnPropertyDescriptor, pe = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? dt(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && lt(e, s, i), i;
};
let X = class extends D {
  constructor() {
    super(), this.cardTitle = "", this.cardSubtitle = "";
  }
  render() {
    return n`
      ${this.cardTitle || this.cardSubtitle ? n`
            <div class="caas-card-header">
              ${this.cardTitle ? n`<h3 class="caas-card-title">${this.cardTitle}</h3>` : ""}
              ${this.cardSubtitle ? n`<p class="caas-card-subtitle">${this.cardSubtitle}</p>` : ""}
              <slot name="header"></slot>
            </div>
          ` : ""}

      <div class="caas-card-content">
        <slot></slot>
      </div>

      <div class="caas-card-footer">
        <slot name="footer"></slot>
      </div>
    `;
  }
};
X.styles = [
  I`
      :host {
        display: block;
        border-radius: var(--caas-radius-base);
        border: 1px solid var(--caas-card-border);
        background: var(--caas-card-background);
        box-shadow: var(--caas-card-shadow);
        overflow: hidden;
      }

      .caas-card-header {
        display: flex;
        padding: var(--caas-spacing-4);
        border-bottom: 1px solid var(--caas-card-border);
        background: var(--caas-card-background);
      }

      .caas-card-title {
        margin: 0;
        font-size: var(--caas-font-size-xl);
        font-weight: var(--caas-font-weight-semibold);
        color: var(--caas-text-primary);
      }

      .caas-card-subtitle {
        margin: var(--caas-spacing-1) 0 0 0;
        font-size: var(--caas-font-size-base);
        color: var(--caas-text-secondary);
      }

      .caas-card-content {
        color: var(--caas-text-primary);
        padding: var(--caas-spacing-4);
      }

      .caas-card-footer {
        color: var(--caas-text-primary);
        padding: var(--caas-spacing-4);
        border-top: 1px solid var(--caas-card-border);
        background: var(--caas-card-background);
      }
    `
];
pe([
  c({ type: String })
], X.prototype, "cardTitle", 2);
pe([
  c({ type: String })
], X.prototype, "cardSubtitle", 2);
X = pe([
  _("caas-card")
], X);
var pt = Object.defineProperty, ht = Object.getOwnPropertyDescriptor, R = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? ht(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && pt(e, s, i), i;
};
let $ = class extends D {
  /**
   * Initialize component with default values
   * Sets up accessible, dismissible dialog behavior by default
   */
  constructor() {
    super(), this._handleKeydown = (t) => {
      t.key === "Escape" && this.open && !this.dialogCloseDisabled && this.closeDialog();
    }, this._handleBackdropClick = (t) => {
      this.closeOnBackdrop && t.target === this && !this.dialogCloseDisabled && this.closeDialog();
    }, this._handleCloseClick = () => {
      this.dialogCloseDisabled || this.closeDialog();
    }, this.title = "", this.open = !1, this.closeOnBackdrop = !1, this.closeOnEscape = !1, this.dialogCloseDisabled = !1;
  }
  /**
   * Lifecycle: Component connected to DOM
   * Sets up global event listeners for keyboard interaction
   */
  connectedCallback() {
    super.connectedCallback(), this.closeOnEscape && document.addEventListener("keydown", this._handleKeydown);
  }
  /**
   * Lifecycle: Component disconnected from DOM
   * Cleans up global event listeners to prevent memory leaks
   */
  disconnectedCallback() {
    super.disconnectedCallback(), document.removeEventListener("keydown", this._handleKeydown);
  }
  /**
   * Opens the dialog programmatically
   * Sets open state and dispatches 'dialog-open' event
   * @public
   */
  openDialog() {
    this.open = !0, this.dispatchEvent(new CustomEvent("dialog-open", { bubbles: !0, composed: !0 }));
  }
  /**
   * Closes the dialog programmatically
   * Sets closed state and dispatches 'dialog-close' event
   * @public
   */
  closeDialog() {
    this.open = !1, this.dispatchEvent(new CustomEvent("dialog-close", { bubbles: !0, composed: !0 }));
  }
  /**
   * @deprecated Use openDialog() instead
   * Maintained for backward compatibility with existing code
   * @public
   */
  show() {
    this.openDialog();
  }
  /**
   * @deprecated Use closeDialog() instead
   * Maintained for backward compatibility with existing code
   * @public
   */
  hide() {
    this.closeDialog();
  }
  /**
   * Renders the dialog content structure
   *
   * Conditional rendering based on title:
   * - With title: Shows header with title and close button
   * - Without title: Shows floating close button over content
   *
   * The dialog content stops event propagation to prevent backdrop clicks
   * when clicking inside the dialog area.
   *
   * @returns TemplateResult containing the dialog HTML structure
   */
  render() {
    return n`
      <div class="dialog-content" @click="${(t) => t.stopPropagation()}">
        <div class="dialog-header">
          <div class="dialog-title-section">
            <h2 class="dialog-title">${this.title}</h2>
            <p class="dialog-subtitle">${this.subtitle}</p>
          </div>
        </div>

        <!-- Floating close button for title-less dialogs -->
        <button
          class="close-button"
          style="position: absolute; top: 24px; right: 24px; z-index: 1; ${this.dialogCloseDisabled ? "display: none;" : ""}"
          @click="${this._handleCloseClick}"
          aria-label="Close dialog"
          ?disabled="${this.dialogCloseDisabled}"
        >
          ×
        </button>

        <!-- Main content area -->
        <div class="dialog-body">
          <slot></slot>
        </div>
      </div>
    `;
  }
  /**
   * Lifecycle: First render completed
   * Sets up backdrop click handling after DOM is ready
   * Must be done after first render to ensure event listener is properly attached
   */
  firstUpdated() {
    this.addEventListener("click", this._handleBackdropClick);
  }
};
$.styles = I`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.1s ease;
    }

    :host([open]) {
      opacity: 1;
      visibility: visible;
    }

    .dialog-content {
      background: var(--caas-dialog-bg);
      border-radius: var(--caas-dialog-border-radius);
      border: var(--caas-dialog-border);
      padding: var(--caas-dialog-padding);
      max-width: var(--caas-dialog-max-width);
      min-width: var(--caas-dialog-min-width);
      max-height: var(--caas-dialog-max-height);
      box-shadow: var(--caas-dialog-shadow), var(--caas-dialog-shadow-2);
      transform: scale(0.95) translateY(10px);
      transition: transform 0.3s ease;
      overflow-y: auto;
    }

    :host([open]) .dialog-content {
      transform: scale(1) translateY(0);
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
    }

    .dialog-title {
      font-size: var(--caas-font-size-xl);
      font-weight: var(--caas-font-weight-semibold);
      color: var(--caas-dialog-title-color);
      margin: 0;
    }

    .dialog-subtitle {
      margin: 0;
      color: var(--caas-dialog-subtitle-color);
      font-size: var(--caas-font-size-base);
      line-height: var(--caas-line-height-normal);
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: var(--caas-spacing-1);
      color: var(--caas-dialog-close-button-color);
      border-radius: var(--caas-radius-sm);
      transition: var(--caas-transition-base);
      flex-shrink: 0;
      font-size: var(--caas-font-size-lg);
      width: 24px;
      height: 24px;
    }

    .close-button:hover {
      background-color: var(--caas-dialog-close-button-bg-hover);
      color: var(--caas-dialog-close-button-color-hover);
    }

    .close-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .close-button:disabled:hover {
      background-color: transparent;
      color: var(--caas-dialog-close-button-color);
    }

    .dialog-body {
      color: var(--caas-dialog-body-color);
      line-height: var(--caas-line-height-normal);
    }
  `;
R([
  c({ type: String })
], $.prototype, "title", 2);
R([
  c({ type: String })
], $.prototype, "subtitle", 2);
R([
  c({ type: Boolean, reflect: !0 })
], $.prototype, "open", 2);
R([
  c({ type: Boolean })
], $.prototype, "closeOnBackdrop", 2);
R([
  c({ type: Boolean })
], $.prototype, "closeOnEscape", 2);
R([
  c({ type: Boolean, attribute: "dialog-close-disabled" })
], $.prototype, "dialogCloseDisabled", 2);
$ = R([
  _("caas-dialog")
], $);
var ut = Object.defineProperty, gt = (t, e, s, a) => {
  for (var i = void 0, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = o(e, s, i) || i);
  return i && ut(e, s, i), i;
};
class Ae extends D {
  /**
   * Dispatches a custom event on the specified event target or this element
   * 
   * @param eventName - The name of the event to dispatch
   * @param detail - The detail data to include with the event
   * @param options - Additional event options (bubbles, composed, etc.)
   * @returns The dispatched CustomEvent
   * 
   * @example
   * ```typescript
   * // Simple usage
   * this.emitEvent('user-action', { userId: 123 });
   * 
   * // With custom options
   * this.emitEvent('critical-error', { error: 'Failed' }, { 
   *   bubbles: false,
   *   cancelable: true 
   * });
   * ```
   */
  emitEvent(e, s, a = {}) {
    const i = new CustomEvent(e, {
      detail: s,
      bubbles: !0,
      composed: !0,
      ...a
    });
    return (this.eventTarget || this).dispatchEvent(i), i;
  }
  /**
   * Dispatches multiple events in sequence
   * Useful for complex workflows that need to emit multiple events
   * 
   * @param events - Array of event configurations
   * @returns Array of dispatched CustomEvents
   * 
   * @example
   * ```typescript
   * this.emitEvents([
   *   { name: 'process-start', detail: { processId: 'abc' } },
   *   { name: 'process-progress', detail: { progress: 0 } }
   * ]);
   * ```
   */
  emitEvents(e) {
    return e.map(
      ({ name: s, detail: a, options: i }) => this.emitEvent(s, a, i)
    );
  }
  /**
   * Sets the event target and returns this for method chaining
   * 
   * @param target - The target where events should be dispatched
   * @returns this element for chaining
   * 
   * @example
   * ```typescript
   * const dialog = new MyDialog()
   *   .setEventTarget(parentComponent)
   *   .setAttribute('title', 'My Dialog');
   * ```
   */
  setEventTarget(e) {
    return this.eventTarget = e, this;
  }
  /**
   * Utility method to emit an error event with consistent structure
   * 
   * @param message - Error message
   * @param code - Optional error code
   * @param originalError - Optional original error object
   * 
   * @example
   * ```typescript
   * this.emitError('Failed to connect to wallet', 'WALLET_CONNECTION_ERROR');
   * ```
   */
  emitError(e, s, a) {
    return this.emitEvent("error", {
      message: e,
      code: s,
      originalError: a,
      timestamp: Date.now()
    });
  }
  /**
   * Utility method to emit a success event with consistent structure
   * 
   * @param message - Success message
   * @param data - Optional success data
   * 
   * @example
   * ```typescript
   * this.emitSuccess('Passkey created successfully', { passkeyId: 'abc123' });
   * ```
   */
  emitSuccess(e, s) {
    return this.emitEvent("success", {
      message: e,
      data: s,
      timestamp: Date.now()
    });
  }
  /**
   * Utility method to emit a warning event with consistent structure
   * 
   * @param message - Warning message
   * @param data - Optional warning data
   * 
   * @example
   * ```typescript
   * this.emitWarning('Wallet connection is unstable', { retryCount: 3 });
   * ```
   */
  emitWarning(e, s) {
    return this.emitEvent("warning", {
      message: e,
      data: s,
      timestamp: Date.now()
    });
  }
}
gt([
  c({ type: Object })
], Ae.prototype, "eventTarget");
var ft = Object.defineProperty, mt = Object.getOwnPropertyDescriptor, w = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? mt(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && ft(e, s, i), i;
};
let y = class extends D {
  constructor() {
    super(), this._progressWidth = 100, this._handleClose = () => {
      this.hide();
    }, this._handleMouseEnter = () => {
      this._pauseTimer();
    }, this._handleMouseLeave = () => {
      this._resumeTimer();
    }, this.type = "info", this.position = "bottom-right", this.title = "", this.message = "", this.duration = 4e3, this.closable = !0, this.showProgress = !0, this.visible = !1;
  }
  connectedCallback() {
    super.connectedCallback(), this.duration > 0 && this._startTimer();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._clearTimer();
  }
  _startTimer() {
    this._clearTimer(), this.showProgress && this.duration > 0 && (this._progressWidth = 100, this._progressTimer = window.setInterval(() => {
      this._progressWidth -= 100 / (this.duration / 100), this._progressWidth <= 0 && (this._progressWidth = 0);
    }, 100)), this._timer = window.setTimeout(() => {
      this.hide();
    }, this.duration);
  }
  _clearTimer() {
    this._timer && (clearTimeout(this._timer), this._timer = void 0), this._progressTimer && (clearInterval(this._progressTimer), this._progressTimer = void 0);
  }
  _pauseTimer() {
    this._clearTimer();
  }
  _resumeTimer() {
    this.duration > 0 && this._startTimer();
  }
  show() {
    this.visible = !0, this.duration > 0 && this._startTimer(), this.dispatchEvent(
      new CustomEvent("toast-show", {
        detail: { type: this.type, message: this.message },
        bubbles: !0,
        composed: !0
      })
    );
  }
  hide() {
    this.visible = !1, this._clearTimer(), this.dispatchEvent(
      new CustomEvent("toast-hide", {
        detail: { type: this.type, message: this.message },
        bubbles: !0,
        composed: !0
      })
    ), setTimeout(() => {
      this.remove();
    }, 300);
  }
  _getIcon() {
    switch (this.type) {
      case "success":
        return n`<svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          ></path>
        </svg>`;
      case "error":
        return n`<svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          ></path>
        </svg>`;
      case "warning":
        return n`<svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          ></path>
        </svg>`;
      case "info":
      default:
        return n`<svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clip-rule="evenodd"
          ></path>
        </svg>`;
    }
  }
  render() {
    return n`
      <div class="toast ${this.type}" @mouseenter="${this._handleMouseEnter}" @mouseleave="${this._handleMouseLeave}">
        <div class="icon">${this._getIcon()}</div>
        <div class="content">
          ${this.title ? n`<h4 class="title">${this.title}</h4>` : ""}
          <p class="message">${this.message || n`<slot></slot>`}</p>
        </div>
        ${this.closable ? n`
              <button class="close-button" @click="${this._handleClose}" aria-label="Close toast">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path
                    d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              </button>
            ` : ""}
        ${this.showProgress && this.duration > 0 ? n` <div class="progress-bar" style="transform: scaleX(${this._progressWidth / 100})"></div> ` : ""}
      </div>
    `;
  }
};
y.styles = I`
    :host {
      position: fixed;
      z-index: 10000;
      pointer-events: none;
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 8px;
      background: var(--caas-toast-bg, #ffffff);
      color: var(--caas-toast-color, #374151);
      border-radius: 8px;
      padding: 12px 16px;
      box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
      border: 1px solid var(--caas-toast-border, #e5e7eb);
      min-width: 300px;
      max-width: 400px;
      pointer-events: auto;
      transform: translateY(-20px);
      opacity: 0;
      transition: all 0.3s ease;
    }

    :host([visible]) .toast {
      transform: translateY(0);
      opacity: 1;
    }

    .toast.success {
      background: var(--caas-toast-success-bg, #dcfce7);
      color: var(--caas-toast-success-color, #166534);
      border-color: var(--caas-toast-success-border, #bbf7d0);
    }

    .toast.error {
      background: var(--caas-toast-error-bg, #fef2f2);
      color: var(--caas-toast-error-color, #991b1b);
      border-color: var(--caas-toast-error-border, #fca5a5);
    }

    .toast.warning {
      background: var(--caas-toast-warning-bg, #fffbeb);
      color: var(--caas-toast-warning-color, #92400e);
      border-color: var(--caas-toast-warning-border, #fcd34d);
    }

    .toast.info {
      background: var(--caas-toast-info-bg, #eff6ff);
      color: var(--caas-toast-info-color, #1e40af);
      border-color: var(--caas-toast-info-border, #93c5fd);
    }

    .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .content {
      flex: 1;
    }

    .title {
      font-weight: 600;
      margin: 0 0 4px 0;
      font-size: 14px;
    }

    .message {
      margin: 0;
      font-size: 14px;
      line-height: 1.4;
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      color: currentColor;
      opacity: 0.7;
      border-radius: 4px;
      transition: opacity 0.2s ease;
      flex-shrink: 0;
    }

    .close-button:hover {
      opacity: 1;
    }

    .progress-bar {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      background: currentColor;
      opacity: 0.3;
      transform-origin: left;
      transition: transform linear;
    }

    @media (prefers-color-scheme: dark) {
      .toast {
        background: var(--caas-toast-bg-dark, #1f2937);
        color: var(--caas-toast-color-dark, #f9fafb);
        border-color: var(--caas-toast-border-dark, #374151);
      }

      .toast.success {
        background: var(--caas-toast-success-bg-dark, #064e3b);
        color: var(--caas-toast-success-color-dark, #a7f3d0);
        border-color: var(--caas-toast-success-border-dark, #065f46);
      }

      .toast.error {
        background: var(--caas-toast-error-bg-dark, #7f1d1d);
        color: var(--caas-toast-error-color-dark, #fca5a5);
        border-color: var(--caas-toast-error-border-dark, #991b1b);
      }

      .toast.warning {
        background: var(--caas-toast-warning-bg-dark, #78350f);
        color: var(--caas-toast-warning-color-dark, #fcd34d);
        border-color: var(--caas-toast-warning-border-dark, #92400e);
      }

      .toast.info {
        background: var(--caas-toast-info-bg-dark, #1e3a8a);
        color: var(--caas-toast-info-color-dark, #93c5fd);
        border-color: var(--caas-toast-info-border-dark, #1e40af);
      }
    }

    /* Position styles */
    :host([position='top-left']) {
      top: 20px;
      left: 20px;
    }

    :host([position='top-center']) {
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
    }

    :host([position='top-right']) {
      top: 20px;
      right: 20px;
    }

    :host([position='bottom-left']) {
      bottom: 20px;
      left: 20px;
    }

    :host([position='bottom-center']) {
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
    }

    :host([position='bottom-right']) {
      bottom: 20px;
      right: 20px;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      :host {
        left: 16px !important;
        right: 16px !important;
        transform: none !important;
      }

      .toast {
        min-width: auto;
        max-width: none;
      }
    }
  `;
w([
  c({ type: String })
], y.prototype, "type", 2);
w([
  c({ type: String, reflect: !0 })
], y.prototype, "position", 2);
w([
  c({ type: String })
], y.prototype, "title", 2);
w([
  c({ type: String })
], y.prototype, "message", 2);
w([
  c({ type: Number })
], y.prototype, "duration", 2);
w([
  c({ type: Boolean })
], y.prototype, "closable", 2);
w([
  c({ type: Boolean })
], y.prototype, "showProgress", 2);
w([
  c({ type: Boolean, reflect: !0 })
], y.prototype, "visible", 2);
w([
  f()
], y.prototype, "_progressWidth", 2);
y = w([
  _("caas-toast")
], y);
const Ee = De(Symbol("main"));
class vt {
  constructor() {
    this._toasts = /* @__PURE__ */ new Map(), this._idCounter = 0, this._defaultPosition = "top-right", this._defaultDuration = 4e3;
  }
  /**
   * Set default configuration
   */
  setDefaults(e) {
    e.position && (this._defaultPosition = e.position), e.duration !== void 0 && (this._defaultDuration = e.duration);
  }
  /**
   * Show a generic Toast
   */
  show(e, s = {}) {
    return this._createToast({
      type: "info",
      message: e,
      ...s
    });
  }
  /**
   * Show success Toast
   */
  success(e, s = {}) {
    return this._createToast({
      type: "success",
      message: e,
      ...s
    });
  }
  /**
   * Show error Toast
   */
  error(e, s = {}) {
    return this._createToast({
      type: "error",
      message: e,
      duration: 0,
      // Error messages don't auto-hide by default
      ...s
    });
  }
  /**
   * Show warning Toast
   */
  warning(e, s = {}) {
    return this._createToast({
      type: "warning",
      message: e,
      ...s
    });
  }
  /**
   * Show info Toast
   */
  info(e, s = {}) {
    return this._createToast({
      type: "info",
      message: e,
      ...s
    });
  }
  /**
   * Hide specific Toast
   */
  hide(e) {
    const s = this._toasts.get(e);
    s && s.hide();
  }
  /**
   * Hide all Toasts
   */
  hideAll() {
    this._toasts.forEach((e) => {
      e.hide();
    });
  }
  /**
   * Hide all Toasts of specified type
   */
  hideByType(e) {
    this._toasts.forEach((s) => {
      s.element.type === e && s.hide();
    });
  }
  /**
   * Hide all Toasts at specified position
   */
  hideByPosition(e) {
    this._toasts.forEach((s) => {
      s.element.position === e && s.hide();
    });
  }
  /**
   * Get current number of displayed Toasts
   */
  getCount() {
    return this._toasts.size;
  }
  /**
   * Get number of Toasts of specified type
   */
  getCountByType(e) {
    let s = 0;
    return this._toasts.forEach((a) => {
      a.element.type === e && s++;
    }), s;
  }
  /**
   * Check if there are Toasts of specified type being displayed
   */
  hasType(e) {
    return this.getCountByType(e) > 0;
  }
  /**
   * Create and show Toast
   */
  _createToast(e) {
    const s = `toast-${++this._idCounter}`, a = new y();
    a.type = e.type || "info", a.position = e.position || this._defaultPosition, a.title = e.title || "", a.message = e.message, a.duration = e.duration !== void 0 ? e.duration : this._defaultDuration, a.closable = e.closable !== void 0 ? e.closable : !0, a.showProgress = e.showProgress !== void 0 ? e.showProgress : !0, document.body.appendChild(a);
    const i = () => {
      this._toasts.delete(s), a.removeEventListener("toast-hide", i);
    };
    a.addEventListener("toast-hide", i);
    const r = {
      id: s,
      element: a,
      show: () => a.show(),
      hide: () => a.hide()
    };
    return this._toasts.set(s, r), requestAnimationFrame(() => {
      a.show();
    }), r;
  }
  /**
   * Clean up all Toast instances (usually called when app unmounts)
   */
  destroy() {
    this._toasts.forEach((e) => {
      e.element.remove();
    }), this._toasts.clear(), this._idCounter = 0;
  }
}
const W = new vt(), Ie = (t) => t.slice(0, 6) + "..." + t.slice(-4), bt = (t) => t ? new Date(t).toLocaleDateString("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: !1
}) : "Unknown";
var yt = Object.defineProperty, xt = Object.getOwnPropertyDescriptor, m = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? xt(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && yt(e, s, i), i;
};
let g = class extends Ae {
  /**
   * Initialize component with default closed state
   */
  constructor() {
    super(), this.mainContext = {
      walletProvider: null,
      address: "",
      client: null,
      projectId: "",
      projectSignerAddress: "",
      isAuthenticated: !0,
      // Assume authenticated since auth-guard handles this
      updateAuthState: () => {
      }
      // No-op since auth-guard handles this
    }, this.title = "CAAS Passkey Management", this.subtitle = "Manage your passkeys for secure authentication", this.addressInitializedPasskey = !1, this.passkeyList = [], this.selectedPasskeyId = "", this.isLoggingOut = !1, this.createFirstPasskeyLoading = !1, this.createPasskeyLoading = !1, this.passkeyListLoading = !1, this.initialStateLoading = !1, this.confirmationVisible = !1, this.confirmationFlowId = "create-first-passkey", this.deleteConfirmationVisible = !1, this.deleteConfirmationFlowId = "delete-additional-passkey", this.deleteConfirmationStepIndex = 0, this.passkeyToDelete = "", this.passkeyToDeleteName = "", this.deletePasskeyLoading = !1, this.confirmationStepIndex = 0, this.dialogOpen = !1;
  }
  /**
   * Getter for accessing context data with fallback logic
   * Prioritizes directly passed contextData over internal context
   * @returns Available context data or null if not provided
   * @private
   */
  get availableContext() {
    return this.contextData || null;
  }
  /**
   * Public API method to open the dialog
   * Sets internal state to trigger rendering
   * @public
   */
  open() {
    this.dialogOpen = !0;
  }
  /**
   * Public API method to close the dialog
   * Sets internal state to trigger rendering cleanup
   * @public
   */
  close() {
    this.dialogOpen = !1;
  }
  /**
   * ============================
   * Lifecycle methods
   * ============================
   */
  /**
   * Component connected to DOM - Initialize state and data
   *
   * Workflow:
   * 1. Validates required context data
   * 2. Propagates context to child components via Lit Context
   * 3. Checks if address has passkey initialization
   * 4. Fetches existing passkeys if address is initialized
   *
   * This async initialization ensures the UI reflects the current
   * blockchain state when the dialog opens.
   */
  connectedCallback() {
    if (super.connectedCallback(), !this.contextData) {
      console.warn("Context data is required for PasskeyDialog");
      return;
    }
    this.mainContext = {
      walletProvider: this.contextData.walletProvider,
      address: this.contextData.address,
      client: this.contextData.client,
      projectId: this.contextData.projectId,
      projectSignerAddress: this.contextData.projectSignerAddress,
      isAuthenticated: this.isUserAuthenticated(),
      updateAuthState: () => this.updateAuthState()
    }, this.isUserAuthenticated() && this.refreshInitialState();
  }
  /**
   * Update authentication state in main context
   * @private
   */
  updateAuthState() {
    const t = this.isUserAuthenticated();
    this.mainContext = {
      ...this.mainContext,
      isAuthenticated: t
    }, this.requestUpdate();
  }
  /**
   * Component disconnected from DOM - Cleanup
   * Resets context to prevent memory leaks and stale data
   */
  disconnectedCallback() {
    super.disconnectedCallback(), this.mainContext = {
      walletProvider: null,
      address: "",
      client: null,
      projectId: "",
      projectSignerAddress: "",
      isAuthenticated: !1,
      updateAuthState: () => {
      }
    };
  }
  /**
   * ============================
   * Private helper methods
   * ============================
   */
  async refreshInitialState() {
    try {
      this.initialStateLoading = !0;
      const t = await this.isAddressInitializedPasskey();
      this.addressInitializedPasskey = t, this.addressInitializedPasskey && await this.fetchPasskeyListOfAddress();
    } catch (t) {
      console.error("Error refreshing initial state:", t), this.addressInitializedPasskey = !1;
    } finally {
      this.initialStateLoading = !1;
    }
  }
  /**
   * Checks if the current address has initialized passkey functionality
   *
   * This determines the dialog's initial mode:
   * - false: Show first-time setup UI
   * - true: Show passkey management UI
   *
   * @returns Promise<boolean> - Whether address has passkey initialization
   * @private
   */
  async isAddressInitializedPasskey() {
    return this.contextData.client ? this.contextData.projectId ? this.contextData.address ? await this.contextData.client?.Passkey.isAddressRegistered(
      this.contextData.projectId,
      this.contextData.address
    ) : (console.warn("Address is required for PasskeyDialog"), !1) : (console.warn("Project ID is required for PasskeyDialog"), !1) : (console.warn("Client is required for PasskeyDialog"), !1);
  }
  /**
   * Fetches the list of passkeys associated with the current address
   *
   * Called when the address is confirmed to have passkey initialization.
   * Updates the component's passkeyList state to reflect current blockchain data.
   * This list is used for:
   * - Displaying existing passkeys in the management UI
   * - Enabling passkey selection for new passkey creation
   * - Tracking total passkey count
   *
   * Loading state management:
   * - Sets passkeyListLoading to true during fetch operation
   * - Ensures loading state is cleared regardless of success/failure
   * - Provides visual feedback through skeleton UI in passkey-list component
   *
   * @returns Promise<void> - Updates passkeyList state
   * @private
   */
  async fetchPasskeyListOfAddress() {
    if (!this.contextData.client)
      return console.warn("Client is required for PasskeyDialog"), [];
    if (!this.contextData.projectId)
      return console.warn("Project ID is required for PasskeyDialog"), [];
    try {
      const t = await this.contextData.client?.Passkey.getPasskeysByAddress(
        this.contextData.projectId,
        this.contextData.address
      );
      this.passkeyList = t;
    } catch (t) {
      console.error("Error fetching passkey list:", t), this.passkeyList = [];
    }
  }
  /**
   * ============================
   * Event handlers
   * ============================
   */
  /**
   * Handles first passkey creation workflow
   *
   * This is the entry point for users who haven't set up passkeys yet.
   * The first passkey is special because it doesn't require an existing
   * passkey for signing - it uses the wallet's authenticator instead.
   *
   * Now integrated with confirmation overlay for step-by-step guidance.
   *
   * Workflow:
   * 1. Shows confirmation overlay with first-passkey flow
   * 2. Waits for user confirmation before proceeding
   * 3. Executes each step with real-time status updates
   * 4. Handles errors with retry functionality
   * 5. Closes confirmation on completion or cancellation
   *
   * @private
   */
  async handleCreateFirstPasskeyClick() {
    this.confirmationFlowId = "create-first-passkey", this.confirmationStepIndex = 0, this.confirmationVisible = !0, await this.updateComplete;
    const t = this.shadowRoot?.querySelector("caas-passkey-confirm");
    t && t.resetSteps && t.resetSteps();
  }
  /**
   * Execute the actual first passkey creation workflow
   * Called when user confirms in the confirmation overlay
   * @private
   */
  async executeCreateFirstPasskey() {
    if (this.emitEvent("create-first-passkey", {
      action: "create-first",
      address: this.contextData.address,
      projectId: this.contextData.projectId
    }), !this.contextData.walletProvider)
      throw new Error("Wallet provider is required");
    try {
      this.createFirstPasskeyLoading = !0;
      const t = {
        onStepStart: (s) => {
          this.updateConfirmationStepStatus(s, "in-progress");
        },
        onStepSuccess: (s) => {
          this.updateConfirmationStepStatus(s, "success");
        },
        onStepError: (s, a) => {
          this.updateConfirmationStepStatus(s, "failed"), console.error(`Step ${s} failed:`, a);
        }
      }, e = await this.contextData.client?.Passkey.createFirstPasskey(
        {
          projectId: this.contextData.projectId,
          address: this.contextData.address,
          projectSignerAddress: this.contextData.projectSignerAddress,
          senderAuthenticatorSign: async (s) => {
            try {
              return (await this.contextData.walletProvider?.signTransaction({
                transactionOrPayload: s
              }))?.authenticator;
            } catch (a) {
              throw console.log("Wallet signature error:", a), a;
            }
          }
        },
        t
      );
      W.success("Passkey created successfully"), this.emitSuccess("First passkey created successfully", {
        transactionHash: e?.hash,
        address: this.contextData.address,
        projectId: this.contextData.projectId
      }), this.selectedPasskeyId = "", await this.refreshInitialState();
    } catch (t) {
      console.error("Error creating first passkey: ", t), this.emitError(t.message || "Failed to create first passkey", "CREATE_FIRST_PASSKEY_ERROR", t);
    } finally {
      this.createFirstPasskeyLoading = !1;
    }
  }
  /**
   * Handles additional passkey creation workflow
   *
   * This method creates additional passkeys for addresses that already have
   * passkey initialization. Unlike the first passkey, this requires an existing
   * passkey to be selected for signing the transaction.
   *
   * Now integrated with confirmation overlay for step-by-step guidance.
   *
   * Workflow:
   * 1. Shows confirmation overlay with additional-passkey flow
   * 2. Waits for user confirmation before proceeding
   * 3. Executes each step with real-time status updates
   * 4. Handles errors with retry functionality
   * 5. Closes confirmation on completion or cancellation
   *
   * @private
   */
  async handleCreatePasskeyClick() {
    if (!this.selectedPasskeyId) {
      W.error("Please select a passkey first");
      return;
    }
    this.confirmationFlowId = "create-additional-passkey", this.confirmationStepIndex = 0, this.confirmationVisible = !0, await this.updateComplete;
    const t = this.shadowRoot?.querySelector("caas-passkey-confirm");
    t && t.resetSteps && t.resetSteps();
  }
  /**
   * Execute the actual additional passkey creation workflow
   * Called when user confirms in the confirmation overlay
   * @private
   */
  async executeCreatePasskey() {
    if (this.emitEvent("create-passkey", {
      action: "create",
      address: this.contextData.address,
      projectId: this.contextData.projectId
    }), !this.contextData.walletProvider)
      throw new Error("Wallet provider is required");
    try {
      this.createPasskeyLoading = !0;
      const t = this.passkeyList.find((a) => a.credentialId === this.selectedPasskeyId);
      if (!t)
        throw new Error("Passkey not found");
      const e = {
        onStepStart: (a) => {
          this.updateConfirmationStepStatus(a, "in-progress");
        },
        onStepSuccess: (a) => {
          this.updateConfirmationStepStatus(a, "success");
        },
        onStepError: (a, i) => {
          this.updateConfirmationStepStatus(a, "failed"), console.error(`Step ${a} failed:`, i);
        }
      }, s = await this.contextData.client?.Passkey.createAdditionalPasskey(
        {
          projectId: this.contextData.projectId,
          address: this.contextData.address,
          selectedPasskey: t,
          projectSignerAddress: this.contextData.projectSignerAddress,
          senderAuthenticatorSign: async (a) => {
            try {
              return (await this.contextData.walletProvider?.signTransaction({
                transactionOrPayload: a
              }))?.authenticator;
            } catch (i) {
              throw console.log("Wallet signature error:", i.message), i;
            }
          }
        },
        e
      );
      W.success("Passkey created successfully"), this.emitSuccess("Passkey created successfully", {
        transactionHash: s?.hash,
        address: this.contextData.address,
        projectId: this.contextData.projectId,
        credentialId: this.selectedPasskeyId
      }), this.selectedPasskeyId = "", await this.fetchPasskeyListOfAddress();
    } catch (t) {
      console.error("Error creating passkey: ", t), this.emitError(t.message ?? "Failed to create passkey", "CREATE_PASSKEY_ERROR", t);
    } finally {
      this.createPasskeyLoading = !1;
    }
  }
  /**
   * Update confirmation step status
   * @private
   */
  updateConfirmationStepStatus(t, e) {
    const s = this.shadowRoot?.querySelector("caas-passkey-confirm");
    s && s.updateStepStatus && s.updateStepStatus(t, e);
  }
  /**
   * Handle confirmation step actions (continue/retry)
   * @private
   */
  handleConfirmationStepAction(t) {
    const { action: e, stepId: s } = t.detail;
    e === "retry" && this.updateConfirmationStepStatus(s, "pending"), this.confirmationFlowId === "create-first-passkey" ? this.executeCreateFirstPasskey() : this.executeCreatePasskey();
  }
  /**
   * Handle confirmation close
   * @private
   */
  handleConfirmationCancel() {
    this.confirmationVisible = !1, this.deleteConfirmationVisible = !1;
  }
  handleConfirmationComplete() {
    this.dispatchEvent(
      new CustomEvent("confirm-complete", {
        bubbles: !0,
        composed: !0
      })
    ), this.confirmationVisible = !1;
    const t = this.shadowRoot?.querySelector("caas-passkey-confirm");
    t && t.resetSteps && t.resetSteps(), this.refreshInitialState();
  }
  /**
   * Update delete confirmation step status
   * @private
   */
  updateDeleteConfirmationStepStatus(t, e) {
    const s = this.shadowRoot?.querySelectorAll("caas-passkey-confirm");
    if (s) {
      const a = Array.from(s).find(
        (i) => i.getAttribute("flow-id")?.startsWith("delete")
      );
      a && a.updateStepStatus && a.updateStepStatus(t, e);
    }
  }
  /**
   * Handle delete confirmation step actions (continue/retry)
   * @private
   */
  handleDeleteConfirmationStepAction(t) {
    const { action: e, stepId: s } = t.detail;
    e === "retry" && this.updateDeleteConfirmationStepStatus(s, "pending"), this.executeDeletePasskey();
  }
  /**
   * Handle delete confirmation close
   * @private
   */
  handleDeleteConfirmationComplete() {
    this.deleteConfirmationVisible = !1;
    const t = this.shadowRoot?.querySelectorAll("caas-passkey-confirm");
    if (t) {
      const e = Array.from(t).find(
        (s) => s.getAttribute("flow-id")?.startsWith("delete")
      );
      e && e.resetSteps && e.resetSteps();
    }
    this.passkeyToDelete = "", this.passkeyToDeleteName = "", this.refreshInitialState(), this.dispatchEvent(
      new CustomEvent("confirm-complete", {
        bubbles: !0,
        composed: !0,
        detail: {
          flowId: this.deleteConfirmationFlowId
        }
      })
    );
  }
  /**
   * Handles dialog close events from the underlying caas-dialog component
   *
   * This method synchronizes the internal dialog state when the underlying
   * dialog component closes (via backdrop click, ESC key, or close button).
   * It ensures that the PasskeyDialog's state remains consistent and properly
   * notifies parent components.
   *
   * Security considerations:
   * - Prevents closing during active confirmation flows (create/delete)
   * - Ensures users complete or cancel ongoing operations before closing
   * - Maintains data integrity during multi-step workflows
   *
   * Workflow:
   * 1. Checks if confirmation flows are active
   * 2. Blocks closure if confirmations are visible
   * 3. Updates internal dialogOpen state to reflect closure
   * 4. Emits 'dialog-close' event to notify parent components
   *
   * Event delegation pattern:
   * The underlying caas-dialog handles user interactions (backdrop, ESC),
   * while this handler manages state synchronization and parent notification.
   *
   * @private
   */
  handleDialogClose() {
    this.confirmationVisible || this.deleteConfirmationVisible || (this.dialogOpen = !1, this.emitEvent("dialog-close"));
  }
  /**
   * Handle successful login
   * @private
   */
  handleLoginSuccess(t) {
    console.log("Login successful:", t.detail), this.refreshInitialState(), this.dispatchEvent(
      new CustomEvent("login-success", {
        detail: t.detail,
        bubbles: !0,
        composed: !0
      })
    );
  }
  /**
   * Handle login error
   * @private
   */
  handleLoginError(t) {
    console.error("Login failed:", t.detail), this.dispatchEvent(
      new CustomEvent("login-error", {
        detail: t.detail,
        bubbles: !0,
        composed: !0
      })
    );
  }
  /**
   * Handle logout request
   * @private
   */
  async handleLogout() {
    try {
      this.isLoggingOut = !0, await this.contextData?.client?.Passkey.LoginManager.logout(), this.updateAuthState(), this.resetDialogState(), this.requestUpdate(), this.dispatchEvent(
        new CustomEvent("logout-success", {
          bubbles: !0,
          composed: !0
        })
      );
    } catch (t) {
      console.error("Logout failed:", t), this.dispatchEvent(
        new CustomEvent("logout-error", {
          detail: { error: t, message: t instanceof Error ? t.message : "Logout failed" },
          bubbles: !0,
          composed: !0
        })
      );
    } finally {
      this.isLoggingOut = !1;
    }
  }
  /**
   * Get current user address for display
   * @private
   */
  getCurrentUserAddress() {
    const t = this.contextData?.walletProvider?.account?.address?.toString() || "";
    return t.length > 10 ? `${t.slice(0, 6)}...${t.slice(-4)}` : t;
  }
  /**
   * Reset dialog state to initial values
   * Called when user logs out or address changes
   * @private
   */
  resetDialogState() {
    this.addressInitializedPasskey = !1, this.passkeyList = [], this.selectedPasskeyId = "", this.createFirstPasskeyLoading = !1, this.createPasskeyLoading = !1, this.passkeyListLoading = !1, this.initialStateLoading = !1, this.deletePasskeyLoading = !1, this.confirmationVisible = !1, this.deleteConfirmationVisible = !1, this.confirmationStepIndex = 0, this.deleteConfirmationStepIndex = 0, this.passkeyToDelete = "", this.passkeyToDeleteName = "", this.requestUpdate();
  }
  /**
   * Handles passkey selection events from the passkey list component
   *
   * This method manages the selection state when users click on passkeys
   * in the management interface. The selected passkey is required for
   * creating additional passkeys and other operations that need authorization
   * from an existing passkey.
   *
   * Event flow:
   * 1. Receives selection event from caas-passkey-list component
   * 2. Updates internal selectedPasskeyId state
   * 3. Propagates selection event to parent components
   *
   * State management:
   * - Only one passkey can be selected at a time
   * - Selection is cleared when creating new passkeys
   * - Selection enables/disables the "Add New Passkey" button
   *
   * @param event - Custom event containing the selected passkey ID
   * @private
   */
  handleSelectPasskey(t) {
    this.selectedPasskeyId = t.detail.credentialId, this.emitEvent("select-passkey", {
      credentialId: t.detail.credentialId
    });
  }
  /**
   * Handles passkey deletion workflow
   *
   * This method initiates the deletion confirmation flow when users request
   * to delete a passkey. It shows a step-by-step confirmation interface
   * rather than immediately deleting the passkey.
   *
   * Workflow:
   * 1. Validates the passkey exists in current list
   * 2. Determines if this is the last passkey (affects flow type)
   * 3. Shows delete confirmation overlay with appropriate flow
   * 4. Waits for user confirmation before proceeding
   *
   * @param event - Custom event containing the passkey ID to delete
   * @private
   */
  async handleDeletePasskey(t) {
    const e = this.passkeyList.find((a) => a.credentialId === t.detail.credentialId);
    if (!e) {
      W.error("Passkey not found");
      return;
    }
    this.passkeyToDelete = t.detail.credentialId, this.passkeyToDeleteName = `Passkey ${this.passkeyList.indexOf(e) + 1}`;
    const s = this.passkeyList.length === 1;
    this.deleteConfirmationFlowId = s ? "delete-last-passkey" : "delete-additional-passkey", this.deleteConfirmationStepIndex = 0, this.deleteConfirmationVisible = !0, this.updateComplete.then(() => {
      const a = this.shadowRoot?.querySelectorAll("caas-passkey-confirm");
      if (a) {
        const i = Array.from(a).find(
          (r) => r.getAttribute("flow-id")?.startsWith("delete")
        );
        i && i.resetSteps && i.resetSteps();
      }
    });
  }
  /**
   * Execute the actual passkey deletion workflow
   * Called when user confirms in the delete confirmation overlay
   * @private
   */
  async executeDeletePasskey() {
    const t = this.passkeyList.find((e) => e.credentialId === this.passkeyToDelete);
    if (!t)
      throw new Error("Passkey not found");
    if (!this.contextData.walletProvider)
      throw new Error("Wallet provider is required");
    try {
      this.deletePasskeyLoading = !0;
      const e = {
        onStepStart: (s) => {
          this.updateDeleteConfirmationStepStatus(s, "in-progress");
        },
        onStepSuccess: (s) => {
          this.updateDeleteConfirmationStepStatus(s, "success");
        },
        onStepError: (s, a) => {
          this.updateDeleteConfirmationStepStatus(s, "failed"), console.error(`Step ${s} failed:`, a);
        }
      };
      await this.contextData.client?.Passkey.removePasskey(
        {
          projectId: this.contextData.projectId,
          address: this.contextData.address,
          selectedPasskey: t,
          // The passkey to be deleted
          projectSignerAddress: this.contextData.projectSignerAddress,
          // Wallet signing function for transaction authorization
          senderAuthenticatorSign: async (s) => {
            try {
              return (await this.contextData.walletProvider?.signTransaction({
                transactionOrPayload: s
              }))?.authenticator;
            } catch (a) {
              throw console.log("Wallet signature error:", a.message), a;
            }
          }
        },
        e
      ), this.deleteConfirmationFlowId === "delete-last-passkey" ? this.updateDeleteConfirmationStepStatus("ACCOUNT_RECOVERY_SETUP", "success") : this.updateDeleteConfirmationStepStatus("DELETION_CONFIRMATION", "success"), W.success("Passkey deleted successfully"), this.selectedPasskeyId = "", this.fetchPasskeyListOfAddress();
    } catch (e) {
      throw console.error("Error deleting passkey:", e), e;
    } finally {
      this.deletePasskeyLoading = !1;
    }
  }
  // ==============================================================
  // Render methods
  // ==============================================================
  /**
   * Renders the complete passkey dialog interface
   *
   * This method creates a dynamic, context-aware interface that adapts based
   * on the current passkey initialization state. It provides two distinct
   * user experiences optimized for different workflow stages.
   *
   * Conditional UI Rendering:
   *
   * 1. First-time Setup Mode (addressInitializedPasskey = false):
   *    - Shows welcome interface with large icon and call-to-action
   *    - Displays "Create Your First Passkey" workflow
   *    - Provides educational messaging about passkey benefits
   *    - Single prominent action button for first passkey creation
   *
   * 2. Management Mode (addressInitializedPasskey = true):
   *    - Shows comprehensive passkey management interface
   *    - Displays current passkey list with selection capability
   *    - Shows address and connection status in header
   *    - Provides "Add New Passkey" action (requires selection)
   *    - Includes passkey count in section title
   *
   * Component Integration:
   * - Uses caas-dialog as the foundational modal container
   * - Embeds caas-passkey-list for passkey management
   * - Integrates caas-button for consistent action styling
   * - Handles all event delegation through established patterns
   *
   * Data Processing:
   * - Maps passkey list data to include derived fields (address, metadata)
   * - Processes extra_data through CAAS SDK deserialization
   * - Restores addresses from public keys for display
   * - Manages selection state through controlled components
   *
   * Accessibility:
   * - Proper semantic HTML structure
   * - ARIA labels and roles for interactive elements
   * - Focus management through underlying dialog component
   * - Screen reader friendly content organization
   *
   * Visual Design:
   * - Custom header with branded icon and descriptive text
   * - Progressive disclosure based on user state
   * - Consistent spacing and typography throughout
   * - Mobile-responsive layout considerations
   *
   * @returns TemplateResult containing the complete dialog interface
   */
  render() {
    const t = this.confirmationVisible || this.deleteConfirmationVisible;
    return n`
      <caas-dialog
        .closeOnBackdrop=${!1}
        .closeOnEscape=${!1}
        .dialogCloseDisabled=${t}
        .open=${this.dialogOpen}
        .title=${this.title}
        .subtitle=${this.subtitle}
        @dialog-close=${this.handleDialogClose}
      >
        <div class="dialog-body">
          ${this.isUserAuthenticated() ? this.renderPasskeyManagementUI() : this.renderAuthenticationUI()}
        </div>
        <div class="powered-by-caas">Powered by CAAS</div>
      </caas-dialog>
    `;
  }
  /**
   * Check if user is authenticated with current wallet address
   * @private
   */
  isUserAuthenticated() {
    return !this.contextData?.client?.Passkey.LoginManager || !this.contextData?.walletProvider?.account?.address ? !1 : this.contextData.client.Passkey.LoginManager.isAuthenticatedWithAddress(
      this.contextData.walletProvider.account.address.toString()
    );
  }
  /**
   * Render authentication UI for unauthenticated users
   * @private
   */
  renderAuthenticationUI() {
    return n`
      <div class="auth-section">
        <div class="auth-login-icon">
          <svg
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              fill="none"
              d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"
            />
            <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
          </svg>
        </div>
        <h3 class="auth-login-title">Sign In to Manage Passkeys</h3>
        <p class="auth-login-description">Please sign in with your wallet to access passkey management</p>
        <caas-login-button @login-success="${this.handleLoginSuccess}" @login-error="${this.handleLoginError}">
        </caas-login-button>
      </div>
    `;
  }
  /**
   * Render passkey management UI for authenticated users
   * @private
   */
  renderPasskeyManagementUI() {
    const t = this.confirmationVisible || this.deleteConfirmationVisible;
    return n`
      <!-- Logout Button Section -->
      <div class="auth-header">
        <div class="auth-user-info">
          <span class="auth-status-dot"></span>
          <span>Signed in as:</span>
          <span class="auth-user-address">
            ${this.getCurrentUserAddress()}
          </span>
        </div>
        <div class="auth-logout-section">
          <caas-button
            size="small"
            variant="outline"
            .loading="${this.isLoggingOut}"
            .disabled="${this.isLoggingOut}"
            @click="${this.handleLogout}">
            ${this.isLoggingOut ? "Signing out..." : "Sign Out"}
          </caas-button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content ${t ? "dimmed" : ""}">
              ${this.initialStateLoading ? n`
                      <!-- Initial Loading State -->
                      <div class="loading-section">
                        <div class="loading-content">
                          <div class="loading-icon">
                            <div class="loading-spinner"></div>
                          </div>
                          <h3 class="loading-title">Loading Passkey Data</h3>
                          <p class="loading-description">Checking your passkey status...</p>
                        </div>
                      </div>
                    ` : this.addressInitializedPasskey ? n`
                        <!-- Existing Passkeys Management -->
                        <div class="passkey-section">
                          <div class="section-title">
                            <span class="section-title-left">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                />
                              </svg>
                              Your Passkeys (${this.passkeyList?.length || 0})
                            </span>

                            <!-- <div class="section-title-right">
                              <span class="info-value"
                                >${Ie(this.availableContext?.address || "") || "No address"}</span
                              >
                              <span
                                class="${this.availableContext?.walletProvider?.isConnected ? "connected-dot" : "disconnected-dot"}"
                              >
                              </span>
                            </div> -->
                          </div>

                          <caas-passkey-list
                            .passkeyList=${this.passkeyList}
                            .selectedPasskeyId=${this.selectedPasskeyId}
                            @select-passkey="${this.handleSelectPasskey}"
                            @delete-passkey="${this.handleDeletePasskey}"
                          ></caas-passkey-list>
                        </div>

                        <div class="action-section">
                          <caas-button
                            size="medium"
                            ?disabled=${!this.selectedPasskeyId}
                            @click="${this.handleCreatePasskeyClick}"
                            ?loading=${this.createPasskeyLoading}
                          >
                            Add New Passkey
                          </caas-button>
                        </div>
                      ` : n`
                        <!-- First Passkey Creation -->
                        <div class="first-passkey-section">
                          <div class="first-passkey-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </div>
                          <h3 class="first-passkey-title">Create Your First Passkey</h3>
                          <p class="first-passkey-description">
                            Get started with secure passkey authentication. Your first passkey will be used to manage
                            additional passkeys.
                          </p>
                          <caas-button
                            size="medium"
                            @click="${this.handleCreateFirstPasskeyClick}"
                            ?loading=${this.createFirstPasskeyLoading}
                          >
                            Create First Passkey
                          </caas-button>
                        </div>
                      `}
            </div>

            <!-- Confirmation Overlay -->
            <div class="confirmation-overlay ${this.confirmationVisible ? "visible" : ""}">
              <div class="confirmation-container">
                <caas-passkey-confirm
                  flow-id="${this.confirmationFlowId}"
                  .currentStepIndex="${this.confirmationStepIndex}"
                  .loading="${this.createFirstPasskeyLoading || this.createPasskeyLoading}"
                  @step-action="${this.handleConfirmationStepAction}"
                  @confirm-cancel="${this.handleConfirmationCancel}"
                  @confirm-complete="${this.handleConfirmationComplete}"
                ></caas-passkey-confirm>
              </div>
            </div>

            <!-- Delete Confirmation Overlay -->
            <div class="confirmation-overlay ${this.deleteConfirmationVisible ? "visible" : ""}">
              <div class="confirmation-container">
                <caas-passkey-confirm
                  flow-id="${this.deleteConfirmationFlowId}"
                  .currentStepIndex="${this.deleteConfirmationStepIndex}"
                  .passkeyName="${this.passkeyToDeleteName}"
                  .loading="${this.deletePasskeyLoading}"
                  @step-action="${this.handleDeleteConfirmationStepAction}"
                  @confirm-cancel="${this.handleConfirmationCancel}"
                  @confirm-complete="${this.handleDeleteConfirmationComplete}"
                ></caas-passkey-confirm>
              </div>
            </div>
          </div>
      `;
  }
};
g.styles = [
  I`
      :host {
        display: block;
      }

      .dialog-body {
        min-height: 60vh;
        border-radius: 8px;
        padding: 8px;
        position: relative;
      }

      /* ===== Confirmation Overlay ===== */
      .confirmation-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--caas-card-background);
        border-radius: inherit;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--caas-spacing-4);
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
      }

      .confirmation-overlay.visible {
        opacity: 1;
        visibility: visible;
      }

      .confirmation-container {
        width: 100%;
        max-width: 480px;
        transform: translateY(20px);
        transition: transform 0.3s ease;
      }

      .confirmation-overlay.visible .confirmation-container {
        transform: translateY(0);
      }

      /* ===== Delete Confirmation Specific Styles ===== */
      .confirmation-overlay:has(caas-passkey-confirm[flow-id^='delete']) {
        background: color-mix(in srgb, var(--caas-card-background) 95%, var(--caas-color-error));
      }

      .confirmation-overlay:has(caas-passkey-confirm[flow-id^='delete']) .confirmation-container {
        border: 1px solid var(--caas-color-error-alpha);
        border-radius: var(--caas-radius-lg);
        box-shadow: 0 4px 20px var(--caas-color-error-alpha);
      }

      /* Enhanced visual feedback for delete operations */
      .confirmation-overlay:has(caas-passkey-confirm[flow-id^='delete'])::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at center, var(--caas-color-error-alpha) 0%, transparent 70%);
        pointer-events: none;
        z-index: -1;
      }

      /* Main content dimming when confirmation is active */
      .main-content {
        transition: opacity 0.3s ease;
      }

      .main-content.dimmed {
        opacity: 0.3;
        pointer-events: none;
      }

      .dialog-icon {
        width: 40px;
        height: 40px;
        background: var(--caas-color-primary);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .dialog-title-section h2 {
        margin: 0 0 var(--caas-spacing-1, 4px) 0;
        color: var(--caas-text-primary);
        font-size: var(--caas-font-size-2xl, 20px);
        font-weight: var(--caas-font-weight-semibold, 600);
        line-height: var(--caas-line-height-tight, 1.2);
      }

      .passkey-info-section {
        background: var(--caas-card-background);
        border: 1px solid var(--caas-card-border);
        border-radius: var(--caas-radius-base, 8px);
        padding: var(--caas-spacing-1, 4px) var(--caas-spacing-2, 8px);
        margin-bottom: var(--caas-spacing-3, 12px);
      }

      .info-row {
        display: grid;
        grid-template-columns: 1fr auto auto;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }

      .info-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
      }

      .connected-dot,
      .disconnected-dot {
        display: inline-block;
        flex-shrink: 0;
        vertical-align: middle;
        width: var(--caas-spacing-2, 8px);
        height: var(--caas-spacing-2, 8px);
        background-color: var(--caas-color-success, #16a34a);
        border-radius: var(--caas-radius-full, 50%);
      }

      .disconnected-dot {
        background-color: var(--caas-color-error, #ef4444);
      }

      .info-label {
        color: var(--caas-text-muted);
        font-weight: var(--caas-font-weight-medium, 500);
      }

      .info-value {
        color: var(--caas-text-primary);
        font-weight: var(--caas-font-weight-medium, 500);
        font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
      }

      .status-indicator {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 500;
      }

      .status-indicator.connected {
        background: var(--caas-status-success-bg);
        color: var(--caas-status-success-text);
      }

      .status-indicator.disconnected {
        background: var(--caas-status-error-bg);
        color: var(--caas-status-error-text);
      }

      .status-indicator.initialized {
        background: var(--caas-status-info-bg);
        color: var(--caas-status-info-text);
      }

      .status-indicator.not-initialized {
        background: var(--caas-status-warning-bg);
        color: var(--caas-status-warning-text);
      }

      .status-icon {
        width: 10px;
        height: 10px;
      }

      .passkey-section {
        margin-bottom: 24px;
      }

      .section-title {
        font-size: var(--caas-font-size-lg, 16px);
        font-weight: var(--caas-font-weight-semibold, 600);
        color: var(--caas-text-primary);
        margin: 0 0 var(--caas-spacing-4, 16px) 0;
        display: flex;
        align-items: center;
        gap: var(--caas-spacing-2, 8px);
        justify-content: space-between;
      }

      .section-title-left {
        display: flex;
        align-items: center;
        gap: var(--caas-spacing-2, 8px);
      }

      .section-title-right {
        display: flex;
        align-items: center;
        gap: var(--caas-spacing-2, 8px);
      }

      .section-title svg {
        width: 18px;
        height: 18px;
        color: var(--caas-color-primary);
      }

      .action-section {
        display: flex;
        justify-content: center;
        margin-top: var(--caas-spacing-6, 24px);
      }

      .first-passkey-section {
        text-align: center;
        padding: var(--caas-spacing-8, 32px) var(--caas-spacing-5, 20px);
      }

      .first-passkey-icon {
        width: 64px;
        height: 64px;
        margin: 0 auto 20px;
        background: var(--caas-color-primary);
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .first-passkey-icon svg {
        width: 32px;
        height: 32px;
        color: white;
      }

      .first-passkey-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--caas-text-primary);
        margin: 0 0 8px 0;
      }

      .first-passkey-description {
        font-size: var(--caas-font-size-base, 14px);
        color: var(--caas-text-muted);
        line-height: var(--caas-line-height-normal, 1.5);
        margin: 0 0 var(--caas-spacing-6, 24px) 0;
        max-width: 340px;
        margin-left: auto;
        margin-right: auto;
      }

      /* Initial loading section */
      .loading-section {
        text-align: center;
        padding: var(--caas-spacing-12, 48px) var(--caas-spacing-5, 20px);
      }

      .loading-content {
        max-width: 320px;
        margin: 0 auto;
      }

      .loading-icon {
        width: var(--caas-spacing-16, 64px);
        height: var(--caas-spacing-16, 64px);
        margin: 0 auto var(--caas-spacing-6, 24px);
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--caas-color-primary);
        border-radius: var(--caas-radius-xl, 16px);
      }

      .loading-spinner {
        width: var(--caas-spacing-8, 32px);
        height: var(--caas-spacing-8, 32px);
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top: 3px solid var(--caas-color-text-inverse, white);
        border-radius: var(--caas-radius-full, 50%);
        animation: spin 1s linear infinite;
      }

      .loading-title {
        font-size: var(--caas-font-size-xl, 18px);
        font-weight: var(--caas-font-weight-semibold, 600);
        color: var(--caas-text-primary);
        margin: 0 0 var(--caas-spacing-2, 8px) 0;
      }

      .loading-description {
        font-size: 14px;
        color: var(--caas-text-muted);
        line-height: 1.5;
        margin: 0;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      /* Mobile responsive */
      @media (max-width: 768px) {
        .dialog-content {
          min-width: auto;
          max-width: 90vw;
        }

        .info-row {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }

        .dialog-header {
          margin-bottom: 20px;
          padding-bottom: 12px;
        }

        .dialog-icon {
          width: 36px;
          height: 36px;
        }

        .dialog-icon svg {
          width: 18px;
          height: 18px;
        }

        .first-passkey-icon {
          width: 56px;
          height: 56px;
          margin-bottom: 16px;
        }

        .first-passkey-icon svg {
          width: 28px;
          height: 28px;
        }
      }

      .powered-by-caas {
        text-align: center;
        font-weight: 500;
        font-size: 12px;
        color: var(--caas-text-muted);
        margin-top: 24px;
      }

      /* ===== Authentication UI Styles ===== */
      .auth-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: var(--caas-spacing-8, 32px) var(--caas-spacing-6, 24px);
        min-height: 320px;
      }

      .auth-login-icon {
        width: 64px;
        height: 64px;
        background: var(--caas-color-primary);
        border-radius: var(--caas-radius-lg, 12px);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: var(--caas-spacing-4, 16px);
      }

      .auth-login-icon svg {
        width: 32px;
        height: 32px;
        color: var(--caas-color-primary-foreground, white);
      }

      .auth-login-title {
        margin: 0 0 var(--caas-spacing-2, 8px) 0;
        color: var(--caas-text-primary);
        font-size: var(--caas-font-size-xl, 18px);
        font-weight: var(--caas-font-weight-semibold, 600);
        line-height: var(--caas-line-height-tight, 1.2);
      }

      .auth-login-description {
        margin: 0 0 var(--caas-spacing-6, 24px) 0;
        color: var(--caas-text-muted);
        font-size: var(--caas-font-size-base, 14px);
        line-height: var(--caas-line-height-relaxed, 1.5);
        max-width: 400px;
      }

      /* ===== Authenticated Header Styles ===== */
      .auth-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--caas-spacing-2, 8px) 0;
        border-bottom: 1px solid var(--caas-border-color);
        margin-bottom: var(--caas-spacing-4, 16px);
      }

      .auth-user-info {
        display: flex;
        align-items: center;
        gap: var(--caas-spacing-2, 8px);
        font-size: var(--caas-font-size-sm, 12px);
        color: var(--caas-text-muted);
      }

      .auth-user-address {
        font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
        color: var(--caas-text-primary);
        font-weight: var(--caas-font-weight-medium, 500);
      }

      .auth-status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--caas-color-success);
      }

      .auth-logout-section {
        display: flex;
        gap: var(--caas-spacing-2, 8px);
      }
    `
];
m([
  Te({ context: Ee })
], g.prototype, "mainContext", 2);
m([
  c({ attribute: !1 })
], g.prototype, "contextData", 2);
m([
  f()
], g.prototype, "dialogOpen", 2);
m([
  c({ type: String, reflect: !0 })
], g.prototype, "title", 2);
m([
  c({ type: String, reflect: !0 })
], g.prototype, "subtitle", 2);
m([
  f()
], g.prototype, "addressInitializedPasskey", 2);
m([
  f()
], g.prototype, "passkeyList", 2);
m([
  f()
], g.prototype, "selectedPasskeyId", 2);
m([
  f()
], g.prototype, "isLoggingOut", 2);
m([
  f()
], g.prototype, "createFirstPasskeyLoading", 2);
m([
  f()
], g.prototype, "createPasskeyLoading", 2);
m([
  f()
], g.prototype, "passkeyListLoading", 2);
m([
  f()
], g.prototype, "initialStateLoading", 2);
m([
  f()
], g.prototype, "confirmationVisible", 2);
m([
  f()
], g.prototype, "confirmationFlowId", 2);
m([
  f()
], g.prototype, "deleteConfirmationVisible", 2);
m([
  f()
], g.prototype, "deleteConfirmationFlowId", 2);
m([
  f()
], g.prototype, "deleteConfirmationStepIndex", 2);
m([
  f()
], g.prototype, "passkeyToDelete", 2);
m([
  f()
], g.prototype, "passkeyToDeleteName", 2);
m([
  f()
], g.prototype, "deletePasskeyLoading", 2);
m([
  f()
], g.prototype, "confirmationStepIndex", 2);
g = m([
  _("caas-passkey-dialog")
], g);
var kt = Object.defineProperty, wt = Object.getOwnPropertyDescriptor, j = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? wt(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && kt(e, s, i), i;
};
let E = class extends C {
  /**
   * Constructor - Initialize component with default values
   */
  constructor() {
    super(), this.dialogElement = null, this.text = "Manage CAAS Passkeys", this.client = null;
  }
  /**
   * Lifecycle method called when the component is connected to the DOM
   * Validates required properties and logs warnings for missing dependencies
   */
  connectedCallback() {
    super.connectedCallback(), this.walletProvider || console.warn("Wallet provider is required for PasskeyButton"), this.address || console.warn("Address is required for PasskeyButton"), this.client || console.warn("CAAS Client is required for PasskeyButton"), this.projectId || console.warn("Project ID is required for PasskeyButton");
  }
  /**
   * Lifecycle method called when the component is disconnected from the DOM
   * Performs cleanup by closing any open dialogs
   */
  disconnectedCallback() {
    super.disconnectedCallback(), this.closeDialog();
  }
  /**
   * Override the render method to add passkey-specific validation
   * @returns TemplateResult containing the button HTML
   */
  render() {
    const t = this.disabled || this.loading || !this.address;
    return n`
      <button
        class="btn ${this.size} primary"
        ?disabled="${t}"
        @click="${this._handlePasskeyClick}"
        aria-label="${this.text || "Manage Passkeys"}"
        aria-busy="${this.loading}"
      >
        ${this.loading ? n`<span class="loading-spinner" aria-hidden="true"></span>` : ""}
        ${this.text ? n`<span>${this.text}</span>` : n`<slot></slot>`}
      </button>
    `;
  }
  /**
   * Handles passkey button click events
   * Validates prerequisites and opens the passkey management dialog
   * @param event - The mouse click event
   * @private
   */
  _handlePasskeyClick(t) {
    if (super._handleClick(t), this.disabled || this.loading) {
      t.preventDefault();
      return;
    }
    if (!this.address) {
      console.error("Address is required for PasskeyButton"), this.dispatchEvent(
        new CustomEvent("error", {
          detail: { message: "Address is required" },
          bubbles: !0,
          composed: !0
        })
      );
      return;
    }
    this.openDialog();
  }
  /**
   * Opens the passkey management dialog
   * Creates a new dialog instance, configures it with current context data,
   * and sets up event listeners for dialog interactions
   * @private
   */
  openDialog() {
    this.dispatchEvent(
      new CustomEvent("dialog-open", {
        bubbles: !0,
        composed: !0
      })
    );
    const t = document.createElement("caas-passkey-dialog");
    t.contextData = {
      walletProvider: this.walletProvider,
      address: this.address,
      client: this.client,
      projectId: this.projectId,
      projectSignerAddress: this.projectSignerAddress
    }, t.eventTarget = this, document.body.appendChild(t), this.dialogElement = t, t.addEventListener("dialog-close", () => {
      this.closeDialog();
    }), t.addEventListener("confirm-complete", (e) => {
      this.dispatchEvent(
        new CustomEvent("confirm-complete", {
          bubbles: !0,
          composed: !0,
          detail: {
            flowId: e.detail?.flowId
          }
        })
      );
    }), setTimeout(() => {
      t.open();
    }, 10);
  }
  /**
   * Closes the passkey management dialog
   * Removes the dialog from the DOM and cleans up references
   * @private
   */
  closeDialog() {
    this.dialogElement && (this.dialogElement.remove(), this.dialogElement = null);
  }
  /**
   * Public method to programmatically open the passkey dialog
   * Useful for external components that need to trigger the dialog
   */
  open() {
    !this.disabled && !this.loading && this.openDialog();
  }
  /**
   * Public method to programmatically close the passkey dialog
   * Useful for external components that need to close the dialog
   */
  close() {
    this.closeDialog();
  }
  /**
   * Public method to check if the button is ready for operation
   * @returns True if all required properties are set and wallet is connected
   */
  isReady() {
    return !!(this.address && this.client && this.projectId && !this.disabled);
  }
};
j([
  c({ type: Object })
], E.prototype, "walletProvider", 2);
j([
  c({ type: String, reflect: !0 })
], E.prototype, "address", 2);
j([
  c({ type: Object })
], E.prototype, "client", 2);
j([
  c({ type: String })
], E.prototype, "projectId", 2);
j([
  c({ type: String })
], E.prototype, "projectSignerAddress", 2);
j([
  f()
], E.prototype, "dialogElement", 2);
E = j([
  _("caas-passkey-button")
], E);
var St = Object.defineProperty, Ct = Object.getOwnPropertyDescriptor, T = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? Ct(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && St(e, s, i), i;
};
let x = class extends D {
  constructor() {
    super(), this.isLoading = !1, this.errorMessage = "", this.text = "Login", this.size = "medium", this.variant = "primary", this.disabled = !1;
  }
  /**
   * Called when the element is added to the DOM
   */
  connectedCallback() {
    super.connectedCallback();
  }
  /**
   * Called when the element is removed from the DOM
   * Cleans up authentication state listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback(), this._cleanupAuthStateListener();
  }
  /**
   * Handles the login click event
   * Initiates wallet-based authentication flow
   */
  async _handleLoginClick() {
    if (!this.mainContext?.client) {
      this.errorMessage = "CAAS client is not initialized";
      return;
    }
    if (!this.mainContext?.walletProvider) {
      this.errorMessage = "Wallet provider is not available";
      return;
    }
    try {
      this.isLoading = !0, this.errorMessage = "";
      const t = await this.mainContext.walletProvider.signMessage({
        message: "Login to CAAS",
        nonce: Date.now().toString(),
        address: !0
      });
      if (!t)
        throw new F("Failed to sign message", "SIGN_MESSAGE_ERROR");
      if (!t.address)
        throw new F("Failed to sign message, address is required", "SIGN_MESSAGE_ERROR");
      if (!t.fullMessage)
        throw new F("Failed to sign message, message is required", "SIGN_MESSAGE_ERROR");
      if (!t.signature)
        throw new F("Failed to sign message, signature is required", "SIGN_MESSAGE_ERROR");
      if (!this.mainContext.walletProvider.account?.publicKey)
        throw new F("Failed to sign message, public key is required", "SIGN_MESSAGE_ERROR");
      await this.mainContext.client.Passkey.LoginManager.login({
        address: t.address,
        signMessage: t.fullMessage,
        publicKey: this.mainContext.walletProvider.account.publicKey.toString(),
        signature: t.signature.toString()
      }), this.mainContext?.updateAuthState(), this.dispatchEvent(
        new CustomEvent("login-success", {
          detail: { address: t.address },
          bubbles: !0,
          composed: !0
        })
      );
    } catch (t) {
      this.errorMessage = t instanceof Error ? t.message : "Login failed", this.dispatchEvent(
        new CustomEvent("login-error", {
          detail: { error: t, message: this.errorMessage },
          bubbles: !0,
          composed: !0
        })
      );
    } finally {
      this.isLoading = !1;
    }
  }
  /**
   * Clean up authentication state listeners
   * LoginManager listeners are automatically cleaned up when component is destroyed
   */
  _cleanupAuthStateListener() {
  }
  /**
   * Render the login/logout button component
   * @returns Template result for the component
   */
  render() {
    return this.mainContext?.isAuthenticated ? "" : n`
      <div class="login-button-wrapper">
        <caas-button
          .size="${this.size}"
          .variant="${this.variant}"
          .text="${this.text}"
          .loading="${this.isLoading}"
          .disabled="${this.disabled || this.isLoading}"
          @click="${this._handleLoginClick}"
        ></caas-button>
        ${this.errorMessage ? n`<div class="error-message">${this.errorMessage}</div>` : ""}
      </div>
    `;
  }
};
x.styles = I`
    :host {
      display: inline-block;
    }

    .login-button-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--caas-spacing-2, 8px);
    }

    .error-message {
      color: var(--caas-color-error, #dc2626);
      font-size: var(--caas-font-size-sm, 14px);
      margin-top: var(--caas-spacing-1, 4px);
    }
  `;
T([
  Le({ context: Ee, subscribe: !0 }),
  f()
], x.prototype, "mainContext", 2);
T([
  c({ type: String })
], x.prototype, "text", 2);
T([
  c({ type: String })
], x.prototype, "size", 2);
T([
  c({ type: String })
], x.prototype, "variant", 2);
T([
  c({ type: Boolean })
], x.prototype, "disabled", 2);
T([
  f()
], x.prototype, "isLoading", 2);
T([
  f()
], x.prototype, "errorMessage", 2);
x = T([
  _("caas-login-button")
], x);
var $t = Object.defineProperty, _t = Object.getOwnPropertyDescriptor, re = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? _t(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && $t(e, s, i), i;
};
let U = class extends D {
  /**
   * Initialize component with default property values
   *
   * Sets up the component in a clean state ready for passkey data.
   * All properties start with safe defaults that won't cause rendering issues.
   */
  constructor() {
    super(), this.passkeyList = [], this.selectedPasskeyId = "", this.loading = !1;
  }
  /**
   * Handles click events on passkey cards for selection management
   *
   * Implements toggle selection behavior where clicking a selected passkey
   * deselects it, and clicking an unselected passkey selects it. Only one
   * passkey can be selected at a time.
   *
   * Event emission:
   * Dispatches a 'select-passkey' custom event with the current selection
   * state for parent components to handle. The event detail contains the
   * selectedPasskeyId (empty string when nothing is selected).
   *
   * @param credentialId - The unique identifier of the clicked passkey
   * @private
   */
  handleCardClick(t) {
    this.selectedPasskeyId === t ? this.selectedPasskeyId = "" : this.selectedPasskeyId = t, this.dispatchEvent(
      new CustomEvent("select-passkey", {
        bubbles: !0,
        composed: !0,
        detail: { credentialId: this.selectedPasskeyId }
      })
    );
  }
  /**
   * Handles delete button click events for passkey removal
   *
   * Prevents the click event from bubbling to the parent card (which would
   * trigger selection) and emits a delete event for the parent component
   * to handle the actual deletion logic.
   *
   * Event flow:
   * 1. Stops event propagation to prevent card selection
   * 2. Dispatches 'delete-passkey' event with credential ID
   * 3. Parent component receives event and handles deletion workflow
   *
   * The actual deletion is handled by parent components as it requires
   * blockchain interaction and transaction signing.
   *
   * @param event - The mouse click event (needs to be stopped)
   * @param credentialId - The unique identifier of the passkey to delete
   * @private
   */
  handleDeleteClick(t, e) {
    t.stopPropagation(), this.dispatchEvent(
      new CustomEvent("delete-passkey", {
        bubbles: !0,
        composed: !0,
        detail: { credentialId: e }
      })
    );
  }
  /**
   * Truncates long passkey IDs for compact display
   *
   * Applies intelligent truncation to long identifiers while preserving
   * shorter IDs in full. Uses the utility sliceAddress function for
   * consistent truncation patterns across the application.
   *
   * Truncation logic:
   * - IDs ≤ 12 characters: Display in full
   * - IDs > 12 characters: Apply address slicing (typically "abc...xyz" format)
   *
   * @param id - The full passkey identifier string
   * @returns Truncated identifier suitable for UI display
   * @private
   */
  sliceId(t) {
    return t.length <= 12 ? t : Ie(t);
  }
  /**
   * Renders skeleton loading placeholders while passkey data is being fetched
   *
   * Creates a realistic preview of the passkey list structure with animated
   * shimmer effects. Shows 3 skeleton cards by default to provide visual
   * feedback during loading states.
   *
   * @returns TemplateResult containing skeleton loading UI
   * @private
   */
  renderSkeleton() {
    return n`
      <div class="passkey-list">
        ${Array.from(
      { length: 3 },
      (t, e) => n`
            <div class="skeleton-card" aria-label="Loading passkey ${e + 1}">
              <div class="skeleton-icon"></div>
              <div class="skeleton-content">
                <div class="skeleton-name"></div>
                <div class="skeleton-details">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-date"></div>
                </div>
              </div>
              <div class="skeleton-actions">
                <div class="skeleton-action-button"></div>
              </div>
            </div>
          `
    )}
      </div>
    `;
  }
  /**
   * Renders the complete passkey list interface with conditional states
   *
   * This method provides a comprehensive rendering strategy that adapts to
   * different data states and user interactions. It ensures users always
   * see appropriate feedback regardless of the current application state.
   *
   * Rendering Logic:
   *
   * 1. Loading State (loading = true):
   *    - Shows animated skeleton placeholders
   *    - Maintains layout structure during data fetching
   *    - Provides visual feedback for ongoing operations
   *
   * 2. Empty State (no passkeys available):
   *    - Displays helpful empty state message
   *    - Includes guidance for creating first passkey
   *    - Uses consistent iconography and messaging
   *
   * 3. Data State (passkeys available):
   *    - Renders interactive passkey cards in a scrollable list
   *    - Shows selection states with visual highlights
   *    - Includes action buttons for passkey management
   *    - Displays metadata (names, dates, IDs)
   *
   * Interactive Features:
   * - Click cards to select/deselect passkeys
   * - Delete buttons with event emission for parent handling
   * - Keyboard accessible with proper ARIA labels
   * - Responsive design for mobile and desktop
   *
   * Data Processing:
   * - Maps raw passkey data to display components
   * - Formats dates and truncates long identifiers
   * - Generates fallback names for unnamed passkeys
   * - Handles selection state styling
   *
   * @returns TemplateResult containing the appropriate UI state
   */
  render() {
    return this.loading ? this.renderSkeleton() : !this.passkeyList || this.passkeyList.length === 0 ? n`
        <div class="empty-state">
          <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h3 class="empty-title">No Passkeys Found</h3>
          <p class="empty-description">You don't have any passkeys yet.</p>
        </div>
      ` : n`
      <div class="passkey-list">
        ${this.passkeyList.map(
      (t) => n`
            <div
              class="passkey-card ${this.selectedPasskeyId === t.credentialId ? "selected" : ""}"
              @click=${() => this.handleCardClick(t.credentialId)}
              role="button"
              tabindex="0"
              aria-label="Select passkey ${t.vendor}"
            >
              <div class="passkey-content">
                <h4 class="passkey-name">
                  <picture class="vendor-icon">
                    <source srcset="${t.vendorIconDark}" media="(prefers-color-scheme: dark)" />
                    <source srcset="${t.vendorIconLight}" media="(prefers-color-scheme: light)" />
                    <img style="width: 100%; height: 100%;" src="${t.vendorIconLight}" alt="${t.vendor}" />
                  </picture>
                  ${t.vendor}
                  <!-- <span class="passkey-id">${this.sliceId(t.credentialId)}</span> -->
                </h4>
                <div class="passkey-details">
                  <span class="passkey-date">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    ${bt(t.createdAt)}
                  </span>
                </div>
              </div>

              <div class="passkey-actions">
                <button
                  class="action-button delete"
                  @click=${(e) => this.handleDeleteClick(e, t.credentialId)}
                  aria-label="Delete passkey"
                  title="Delete passkey"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          `
    )}
      </div>
    `;
  }
};
U.styles = [
  I`
      :host {
        display: block;
      }

      /* ===== Passkey List Container ===== */
      .passkey-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin: 0;
        padding: 0;
        max-height: 400px;
        overflow-y: auto;

        /* Modern scrollbar styling for Firefox */
        scrollbar-width: thin;
        scrollbar-color: var(--caas-scrollbar-thumb) var(--caas-scrollbar-track);
      }

      .vendor-icon {
        width: 16px;
        height: 16px;
      }

      /* Webkit scrollbar customization */
      .passkey-list::-webkit-scrollbar {
        width: var(--caas-scrollbar-width);
      }

      .passkey-list::-webkit-scrollbar-track {
        background: var(--caas-scrollbar-track);
        border-radius: 4px;
      }

      .passkey-list::-webkit-scrollbar-thumb {
        background: var(--caas-scrollbar-thumb);
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      .passkey-list::-webkit-scrollbar-thumb:hover {
        background: var(--caas-scrollbar-thumb-hover);
      }

      .passkey-card {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border: 1px solid var(--caas-card-border);
        border-radius: 12px;
        background: var(--caas-card-background);
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: var(--caas-card-shadow);
        position: relative;
      }

      .passkey-card:hover {
        border-color: var(--caas-color-primary);
        box-shadow: var(--caas-card-shadow-hover);
      }

      .passkey-card.selected {
        border-color: var(--caas-color-primary);
        background: var(--caas-card-background);
        box-shadow: 0 0 0 3px var(--caas-color-primary-alpha);
      }

      .passkey-content {
        flex: 1;
        min-width: 0;
      }

      .passkey-name {
        font-size: 15px;
        font-weight: 600;
        color: var(--caas-text-primary);
        margin: 0 0 6px 0;
        line-height: 1.2;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .passkey-details {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 4px;
        flex-wrap: wrap;
      }

      .passkey-id {
        font-size: 12px;
        font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
        color: var(--caas-text-secondary);
        background: var(--caas-id-background);
        padding: 2px 4px;
        border-radius: 6px;
        border: 1px solid var(--caas-id-border);
      }

      .passkey-date {
        font-size: 12px;
        color: var(--caas-text-muted);
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .passkey-date svg {
        width: 12px;
        height: 12px;
      }

      .passkey-address {
        font-size: 12px;
        font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
        color: var(--caas-text-muted);
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .passkey-address svg {
        width: 12px;
        height: 12px;
      }

      .passkey-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-left: 12px;
      }

      .action-button {
        width: 36px;
        height: 36px;
        border: none;
        background: none;
        border-radius: 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        color: var(--caas-text-muted);
        position: relative;
      }

      .action-button:hover {
        background: var(--caas-button-hover-background);
        transform: scale(1.05);
      }

      .action-button.delete:hover {
        background: var(--caas-button-delete-background);
        color: var(--caas-button-delete-color);
      }

      .action-button svg {
        width: 18px;
        height: 18px;
      }

      .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: var(--caas-text-muted);
      }

      .empty-icon {
        width: 56px;
        height: 56px;
        margin: 0 auto 20px;
        color: var(--caas-text-muted);
        opacity: 0.4;
      }

      .empty-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--caas-text-secondary);
        margin: 0 0 8px 0;
      }

      .empty-description {
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
      }

      /* Selected indicator */
      .passkey-card.selected::before {
        content: '';
        position: absolute;
        left: -1px;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 24px;
        background: var(--caas-color-primary);
        border-radius: 0 2px 2px 0;
      }

      /* Loading skeleton styles */
      .skeleton-card {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border: 1px solid var(--caas-card-border);
        border-radius: 12px;
        background: var(--caas-card-background);
        box-shadow: var(--caas-card-shadow);
        position: relative;
        overflow: hidden;
      }

      .skeleton-icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        margin-right: 12px;
        background: linear-gradient(
          90deg,
          var(--caas-skeleton-start) 25%,
          var(--caas-skeleton-middle) 50%,
          var(--caas-skeleton-start) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
      }

      .skeleton-content {
        flex: 1;
        min-width: 0;
      }

      .skeleton-name {
        height: 18px;
        background: linear-gradient(
          90deg,
          var(--caas-skeleton-start) 25%,
          var(--caas-skeleton-middle) 50%,
          var(--caas-skeleton-start) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 4px;
        margin-bottom: 8px;
        width: 60%;
      }

      .skeleton-details {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 4px;
      }

      .skeleton-id {
        height: 14px;
        width: 80px;
        background: linear-gradient(
          90deg,
          var(--caas-skeleton-start) 25%,
          var(--caas-skeleton-middle) 50%,
          var(--caas-skeleton-start) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 3px;
      }

      .skeleton-date {
        height: 14px;
        width: 60px;
        background: linear-gradient(
          90deg,
          var(--caas-skeleton-start) 25%,
          var(--caas-skeleton-middle) 50%,
          var(--caas-skeleton-start) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 3px;
      }

      .skeleton-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-left: 12px;
      }

      .skeleton-action-button {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: linear-gradient(
          90deg,
          var(--caas-skeleton-start) 25%,
          var(--caas-skeleton-middle) 50%,
          var(--caas-skeleton-start) 75%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
      }

      @keyframes skeleton-loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }

      /* Mobile responsive */
      @media (max-width: 768px) {
        .passkey-card {
          padding: 14px;
        }

        .passkey-details {
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
        }

        .action-button {
          width: 32px;
          height: 32px;
        }
      }
    `
];
re([
  c({ type: Array })
], U.prototype, "passkeyList", 2);
re([
  c({ type: String, reflect: !0 })
], U.prototype, "selectedPasskeyId", 2);
re([
  c({ type: Boolean, reflect: !0 })
], U.prototype, "loading", 2);
U = re([
  _("caas-passkey-list")
], U);
const Pt = {
  "create-first-passkey": {
    id: "create-first-passkey",
    title: "Create Your First Passkey",
    subtitle: "Setting up passkey authentication for your account",
    steps: [
      {
        id: u.CREATE_PASSKEY,
        label: "Create a passkey",
        description: "Generate new passkey credentials",
        status: "pending"
      },
      {
        id: u.WALLET_SIGNATURE,
        label: "Wallet signature",
        description: "Sign transaction with your wallet",
        status: "pending"
      },
      {
        id: u.TRANSACTION_SUBMISSION,
        label: "Transaction submission",
        description: "Submit to blockchain and wait for confirmation",
        status: "pending"
      }
    ],
    actions: {
      inProgress: [
        { label: "Cancel", variant: "outline", action: "cancel" },
        { label: "Continue", variant: "primary", action: "continue" }
      ],
      completed: [{ label: "Complete", variant: "primary", action: "complete" }]
    },
    stepMessages: {
      [u.CREATE_PASSKEY]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please complete the passkey creation process in your browser.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.WALLET_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the transaction with your wallet.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.SELECTED_PASSKEY_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the transaction with the selected passkey.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.TRANSACTION_SUBMISSION]: {
        pending: "Ready to begin this step.",
        "in-progress": "Submitting transaction to blockchain. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      }
    }
  },
  "create-additional-passkey": {
    id: "create-additional-passkey",
    title: "Create New Passkey",
    subtitle: "Adding an additional passkey to your account",
    steps: [
      {
        id: u.CREATE_PASSKEY,
        label: "Create a passkey",
        description: "Generate new passkey credentials",
        status: "pending"
      },
      {
        id: u.SELECTED_PASSKEY_SIGNATURE,
        label: "Selected passkey signature",
        description: "Authorize with existing passkey",
        status: "pending"
      },
      {
        id: u.WALLET_SIGNATURE,
        label: "Wallet signature",
        description: "Sign transaction with your wallet",
        status: "pending"
      },
      {
        id: u.TRANSACTION_SUBMISSION,
        label: "Transaction submission",
        description: "Submit to blockchain and wait for confirmation",
        status: "pending"
      }
    ],
    actions: {
      inProgress: [
        { label: "Cancel", variant: "outline", action: "cancel" },
        { label: "Continue", variant: "primary", action: "continue" }
      ],
      completed: [{ label: "Complete", variant: "primary", action: "complete" }]
    },
    stepMessages: {
      [u.CREATE_PASSKEY]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please complete the passkey creation process in your browser.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.WALLET_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the transaction with your wallet.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.SELECTED_PASSKEY_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the transaction with the selected passkey.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.TRANSACTION_SUBMISSION]: {
        pending: "Ready to begin this step.",
        "in-progress": "Submitting transaction to blockchain. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      }
    }
  },
  "delete-last-passkey": {
    id: "delete-last-passkey",
    title: "Delete Passkey",
    subtitle: "Removing passkey from your account",
    titleColor: "var(--caas-color-error)",
    steps: [
      {
        id: u.WALLET_SIGNATURE,
        label: "Wallet signature",
        description: "Sign deletion transaction with your wallet",
        status: "pending"
      },
      {
        id: u.TRANSACTION_SUBMISSION,
        label: "Transaction submission",
        description: "Submit to blockchain and wait for confirmation",
        status: "pending"
      },
      {
        id: "ACCOUNT_RECOVERY_SETUP",
        label: "Account recovery setup",
        description: "Set up alternative account recovery methods",
        status: "pending"
      }
    ],
    warning: {
      icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z",
      title: "This is your last passkey",
      message: "After deletion, you'll be exposed to a higher risk of losing access to your account."
    },
    actions: {
      inProgress: [
        { label: "Cancel", variant: "outline", action: "cancel" },
        { label: "Delete", variant: "destructive", action: "continue" }
      ],
      completed: [{ label: "Complete", variant: "primary", action: "complete" }]
    },
    stepMessages: {
      [u.WALLET_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the deletion transaction with your wallet.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.SELECTED_PASSKEY_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please authorize the deletion with the selected passkey.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.TRANSACTION_SUBMISSION]: {
        pending: "Ready to begin this step.",
        "in-progress": "Submitting deletion transaction to blockchain. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      ACCOUNT_RECOVERY_SETUP: {
        pending: "Ready to begin this step.",
        "in-progress": "Please set up alternative recovery methods for your account.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      DELETION_CONFIRMATION: {
        pending: "Ready to begin this step.",
        "in-progress": "Confirming passkey removal. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      }
    }
  },
  "delete-additional-passkey": {
    id: "delete-additional-passkey",
    title: "Delete Passkey",
    subtitle: "Removing passkey from your account",
    titleColor: "var(--caas-color-error)",
    steps: [
      {
        id: u.SELECTED_PASSKEY_SIGNATURE,
        label: "Selected passkey signature",
        description: "Authorize deletion with existing passkey",
        status: "pending"
      },
      {
        id: u.WALLET_SIGNATURE,
        label: "Wallet signature",
        description: "Sign deletion transaction with your wallet",
        status: "pending"
      },
      {
        id: u.TRANSACTION_SUBMISSION,
        label: "Transaction submission",
        description: "Submit to blockchain and wait for confirmation",
        status: "pending"
      },
      {
        id: "DELETION_CONFIRMATION",
        label: "Deletion confirmation",
        description: "Confirm passkey has been successfully removed",
        status: "pending"
      }
    ],
    actions: {
      inProgress: [
        { label: "Cancel", variant: "outline", action: "cancel" },
        { label: "Delete", variant: "destructive", action: "continue" }
      ],
      completed: [{ label: "Complete", variant: "primary", action: "complete" }]
    },
    stepMessages: {
      [u.WALLET_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please sign the deletion transaction with your wallet.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.SELECTED_PASSKEY_SIGNATURE]: {
        pending: "Ready to begin this step.",
        "in-progress": "Please authorize the deletion with the selected passkey.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      [u.TRANSACTION_SUBMISSION]: {
        pending: "Ready to begin this step.",
        "in-progress": "Submitting deletion transaction to blockchain. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      ACCOUNT_RECOVERY_SETUP: {
        pending: "Ready to begin this step.",
        "in-progress": "Please set up alternative recovery methods for your account.",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      },
      DELETION_CONFIRMATION: {
        pending: "Ready to begin this step.",
        "in-progress": "Confirming passkey removal. Please wait...",
        success: "Step completed successfully!",
        failed: "Step failed. Click the red icon above to retry."
      }
    }
  }
};
function At(t) {
  return Pt[t];
}
function Rt(t) {
  return t;
}
var Et = Object.defineProperty, It = Object.getOwnPropertyDescriptor, L = (t, e, s, a) => {
  for (var i = a > 1 ? void 0 : a ? It(e, s) : e, r = t.length - 1, o; r >= 0; r--)
    (o = t[r]) && (i = (a ? o(e, s, i) : o(i)) || i);
  return a && i && Et(e, s, i), i;
};
let k = class extends D {
  constructor() {
    super(), this.activeConfig = null, this.internalSteps = [], this.flowId = "", this.flowConfig = null, this.currentStepIndex = 0, this.loading = !1, this.passkeyName = "";
  }
  /**
   * Lifecycle: Component connected to DOM
   */
  connectedCallback() {
    super.connectedCallback(), this.initializeSteps();
  }
  /**
   * Get the active flow configuration
   */
  get config() {
    return this.flowConfig ? this.flowConfig : this.flowId ? At(this.flowId) || null : this.activeConfig;
  }
  /**
   * Get the steps from the active configuration
   */
  get steps() {
    return this.internalSteps.length > 0 ? this.internalSteps : this.config?.steps || [];
  }
  /**
   * Get the current active step
   */
  get currentStep() {
    return this.steps[this.currentStepIndex];
  }
  /**
   * Initialize internal steps from configuration
   */
  initializeSteps() {
    const t = this.config;
    t && t.steps ? this.internalSteps = t.steps.map((e) => ({
      ...e,
      status: "pending"
    })) : this.internalSteps = [];
  }
  /**
   * Reset all steps to pending status
   */
  resetSteps() {
    this.initializeSteps(), this.currentStepIndex = 0;
  }
  /**
   * Update step status programmatically
   */
  updateStepStatus(t, e) {
    this.internalSteps.length === 0 && this.initializeSteps();
    const s = this.internalSteps.findIndex((a) => a.id === t);
    s !== -1 && (this.internalSteps = this.internalSteps.map((a, i) => i === s ? { ...a, status: e } : a), this.dispatchEvent(
      new CustomEvent("step-status-change", {
        bubbles: !0,
        composed: !0,
        detail: { stepId: t, status: e, stepIndex: s }
      })
    ));
  }
  /**
   * Move to the next step in the workflow
   */
  nextStep() {
    this.currentStepIndex < this.steps.length - 1 && this.currentStepIndex++;
  }
  /**
   * Move to the previous step in the workflow
   */
  previousStep() {
    this.currentStepIndex > 0 && this.currentStepIndex--;
  }
  /**
   * Handle step icon click (for retry functionality)
   */
  _handleStepClick(t, e) {
    t.status === "failed" && this.dispatchEvent(
      new CustomEvent("step-action", {
        bubbles: !0,
        composed: !0,
        detail: {
          action: "retry",
          stepId: t.id,
          stepIndex: e
        }
      })
    );
  }
  /**
   * Handle action button click
   */
  _handleAction(t) {
    const e = {
      action: t,
      stepId: this.currentStep?.id,
      stepIndex: this.currentStepIndex
    };
    t === "continue" ? this.dispatchEvent(
      new CustomEvent("step-action", {
        bubbles: !0,
        composed: !0,
        detail: e
      })
    ) : t === "cancel" ? this.loading || this.dispatchEvent(
      new CustomEvent("confirm-cancel", {
        bubbles: !0,
        composed: !0,
        detail: e
      })
    ) : t === "complete" && this.dispatchEvent(
      new CustomEvent("confirm-complete", {
        bubbles: !0,
        composed: !0,
        detail: e
      })
    );
  }
  /**
   * Render step icon based on status
   */
  _renderStepIcon(t, e) {
    const s = `step-icon ${t.status}`;
    switch (t.status) {
      case "pending":
        return n`<div class="${s}">${e + 1}</div>`;
      case "in-progress":
        return n`<div class="${s}"><div class="step-spinner"></div></div>`;
      case "success":
        return n`
          <div class="${s}">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        `;
      case "failed":
        return n`
          <div
            class="${s}"
            @click="${() => this._handleStepClick(t, e)}"
            title="Click to retry"
            role="button"
            tabindex="0"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        `;
    }
  }
  /**
   * Get the appropriate message for the current step status
   */
  _getCurrentStepMessage() {
    const t = this.currentStep, e = this.config;
    if (!t || !e) return "";
    const s = e.stepMessages?.[t.id]?.[t.status];
    if (s)
      return s;
    switch (t.status) {
      case "pending":
        return "Ready to begin this step.";
      case "in-progress":
        return "Processing...";
      case "success":
        return "Step completed successfully!";
      case "failed":
        return "Step failed. Click the red icon above to retry.";
      default:
        return "";
    }
  }
  /**
   * Check if all steps are completed
   */
  _allStepsCompleted() {
    return this.steps.every((t) => t.status === "success");
  }
  /**
   * Lifecycle: Property changed
   */
  willUpdate(t) {
    (t.has("flowId") || t.has("flowConfig")) && (this.activeConfig = this.config, this.initializeSteps(), this.currentStepIndex = 0);
  }
  /**
   * Render the complete passkey confirmation interface
   */
  render() {
    const t = this.config;
    if (!t)
      return n`<div>No configuration provided</div>`;
    const e = this.steps, s = this.currentStep, i = this._allStepsCompleted() ? t.actions.completed : t.actions.inProgress;
    return n`
      <div class="confirm-container">
        <!-- Header -->
        <div class="confirm-header">
          <h3
            class="confirm-title ${t.titleColor === "var(--caas-color-error)" ? "error" : ""}"
            ${t.titleColor ? `style="color: ${t.titleColor}"` : ""}
          >
            ${t.title}
          </h3>
          <p class="confirm-subtitle">
            Step ${this.currentStepIndex + 1} of ${e.length} -
            ${this.passkeyName ? n`${t.subtitle.replace("passkey", "")} <span class="passkey-name">${this.passkeyName}</span>` : t.subtitle}
          </p>
        </div>

        <!-- Warning Section -->
        ${t.warning ? n`
              <div class="warning-section">
                <div class="warning-icon">
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${t.warning.icon}" />
                  </svg>
                </div>
                <h4 class="warning-title">${t.warning.title}</h4>
                <p class="warning-message">${t.warning.message}</p>
              </div>
            ` : ""}

        <!-- Steps Progress -->
        <div class="steps-container">
          <ol class="steps-list">
            ${e.map(
      (r, o) => n`
                <li class="step-item ${r.status}" ?data-current="${o === this.currentStepIndex}">
                  ${this._renderStepIcon(r, o)}

                  <div class="step-content">
                    <h4 class="step-label">${r.label}</h4>
                    ${r.description ? n` <p class="step-description">${r.description}</p> ` : ""}
                  </div>
                </li>
              `
    )}
          </ol>
        </div>

        <!-- Current Step Information -->
        ${s ? n`
              <div class="current-step-info">
                <h4 class="current-step-title">${s.label}</h4>
                <p class="current-step-message">${this._getCurrentStepMessage()}</p>
              </div>
            ` : ""}

        <!-- Actions -->
        <div class="actions-section">
          ${i.map(
      (r) => n`
              <caas-button
                size="small"
                variant="${r.variant || "primary"}"
                @click="${() => this._handleAction(r.action)}"
                ?disabled="${r.action !== "complete" && (this.loading || r.action === "continue" && s?.status === "success")}"
                ?loading="${this.loading && r.action === "continue"}"
              >
                ${r.action === "continue" && s?.status === "failed" ? "Retry" : r.label}
              </caas-button>
            `
    )}
        </div>
      </div>
    `;
  }
};
k.styles = [
  I`
      :host {
        display: block;
      }

      .confirm-container {
        background: var(--caas-card-background);
        border-radius: var(--caas-radius-lg);
        padding: var(--caas-spacing-6);
        border: 1px solid var(--caas-card-border);
        box-shadow: var(--caas-card-shadow);
      }

      /* ===== Header Section ===== */
      .confirm-header {
        text-align: center;
        margin-bottom: var(--caas-spacing-6);
        padding-bottom: var(--caas-spacing-4);
        border-bottom: 1px solid var(--caas-card-border);
      }

      .confirm-title {
        font-size: var(--caas-font-size-xl);
        font-weight: var(--caas-font-weight-semibold);
        color: var(--caas-text-primary);
        margin: 0 0 var(--caas-spacing-2) 0;
      }

      .confirm-title.error {
        color: var(--caas-color-error);
      }

      .confirm-subtitle {
        font-size: var(--caas-font-size-base);
        color: var(--caas-text-secondary);
        margin: 0;
        line-height: var(--caas-line-height-normal);
      }

      .passkey-name {
        font-weight: var(--caas-font-weight-medium);
        color: var(--caas-text-primary);
      }

      /* ===== Warning Section ===== */
      .warning-section {
        background: var(--caas-color-error-alpha);
        border: 1px solid var(--caas-color-error-alpha);
        border-radius: var(--caas-radius-base);
        padding: var(--caas-spacing-4);
        margin-bottom: var(--caas-spacing-6);
        text-align: center;
      }

      .warning-icon {
        display: flex;
        justify-content: center;
        margin-bottom: var(--caas-spacing-2);
        color: var(--caas-color-error);
      }

      .warning-title {
        font-size: var(--caas-font-size-base);
        font-weight: var(--caas-font-weight-semibold);
        color: var(--caas-color-error);
        margin: 0 0 var(--caas-spacing-1) 0;
      }

      .warning-message {
        font-size: var(--caas-font-size-sm);
        color: var(--caas-text-secondary);
        margin: 0;
        line-height: var(--caas-line-height-normal);
      }

      /* ===== Steps Section ===== */
      .steps-container {
        margin-bottom: var(--caas-spacing-6);
      }

      .steps-list {
        position: relative;
        padding: 0;
        margin: 0;
        list-style: none;
      }

      .step-item {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: var(--caas-spacing-3);
        padding: var(--caas-spacing-3) 0;
        transition: all 0.3s ease;
      }

      .step-item:not(:last-child) {
        padding-bottom: var(--caas-spacing-4);
      }

      /* Connecting line between steps */
      .step-item:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 18px;
        top: 48px;
        width: 2px;
        height: calc(100% - 24px);
        background: var(--caas-card-border);
        transition: background-color 0.3s ease;
      }

      .step-item.completed::after {
        background: var(--caas-color-success);
      }

      /* ===== Step Icon ===== */
      .step-icon {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: var(--caas-radius-full);
        flex-shrink: 0;
        font-weight: var(--caas-font-weight-semibold);
        font-size: var(--caas-font-size-sm);
        transition: all 0.3s ease;
        z-index: 1;
      }

      /* Pending state */
      .step-icon.pending {
        background: var(--caas-card-background);
        border: 2px solid var(--caas-card-border);
        color: var(--caas-text-muted);
      }

      /* In-progress state */
      .step-icon.in-progress {
        background: var(--caas-color-primary);
        border: 2px solid var(--caas-color-primary);
        color: var(--caas-icon-color);
      }

      /* Success state */
      .step-icon.success {
        background: var(--caas-color-success);
        border: 2px solid var(--caas-color-success);
        color: var(--caas-icon-color);
      }

      /* Failed state */
      .step-icon.failed {
        background: var(--caas-color-error);
        border: 2px solid var(--caas-color-error);
        color: var(--caas-icon-color);
        cursor: pointer;
      }

      .step-icon.failed:hover {
        background: var(--caas-color-primary-hover);
        border-color: var(--caas-color-primary-hover);
      }

      /* Loading spinner for in-progress state */
      .step-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top: 2px solid var(--caas-icon-color);
        border-radius: var(--caas-radius-full);
        animation: spin 1s linear infinite;
      }

      /* Step content */
      .step-content {
        flex: 1;
        min-width: 0;
        padding-top: var(--caas-spacing-1);
      }

      .step-label {
        font-size: var(--caas-font-size-base);
        font-weight: var(--caas-font-weight-medium);
        color: var(--caas-text-primary);
        margin: 0 0 var(--caas-spacing-1) 0;
        line-height: var(--caas-line-height-tight);
      }

      .step-description {
        font-size: var(--caas-font-size-sm);
        color: var(--caas-text-muted);
        margin: 0;
        line-height: var(--caas-line-height-normal);
      }

      .step-item.in-progress .step-label {
        color: var(--caas-color-primary);
      }

      .step-item.success .step-label {
        color: var(--caas-color-success);
      }

      .step-item.failed .step-label {
        color: var(--caas-color-error);
      }

      /* ===== Current Step Info ===== */
      .current-step-info {
        background: var(--caas-color-primary-alpha);
        border: 1px solid var(--caas-color-primary-alpha);
        border-radius: var(--caas-radius-base);
        padding: var(--caas-spacing-4);
        margin-bottom: var(--caas-spacing-6);
        text-align: center;
      }

      .current-step-title {
        font-size: var(--caas-font-size-base);
        font-weight: var(--caas-font-weight-medium);
        color: var(--caas-text-primary);
        margin: 0 0 var(--caas-spacing-1) 0;
      }

      .current-step-message {
        font-size: var(--caas-font-size-sm);
        color: var(--caas-text-secondary);
        margin: 0;
        line-height: var(--caas-line-height-normal);
      }

      /* ===== Actions Section ===== */
      .actions-section {
        display: flex;
        justify-content: center;
        gap: var(--caas-spacing-3);
        padding-top: var(--caas-spacing-4);
        border-top: 1px solid var(--caas-card-border);
      }

      /* ===== Animations ===== */
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes fadeIn {
        0% {
          opacity: 0;
          transform: translateY(10px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .step-item {
        animation: fadeIn 0.3s ease;
      }

      /* ===== Mobile Responsive ===== */
      @media (max-width: 375px) {
        :host {
          padding: var(--caas-spacing-4);
        }

        .confirm-header {
          margin-bottom: var(--caas-spacing-4);
          padding-bottom: var(--caas-spacing-3);
        }

        .confirm-title {
          font-size: var(--caas-font-size-lg);
        }

        .step-icon {
          width: 32px;
          height: 32px;
        }

        .step-item:not(:last-child)::after {
          left: 15px;
          top: 44px;
        }

        .actions-section {
          flex-direction: column;
          gap: var(--caas-spacing-2);
        }
      }

      /* ===== Accessibility ===== */
      @media (prefers-reduced-motion: reduce) {
        .step-spinner {
          animation: none;
        }

        .step-item {
          animation: none;
        }

        * {
          transition: none !important;
        }
      }

      @media (prefers-contrast: high) {
        .step-icon {
          border-width: 3px;
        }

        .current-step-info {
          border-width: 2px;
        }

        .warning-section {
          border-width: 2px;
        }
      }
    `
];
L([
  c({ type: String, attribute: "flow-id" })
], k.prototype, "flowId", 2);
L([
  c({ type: Object })
], k.prototype, "flowConfig", 2);
L([
  c({ type: Number, reflect: !0 })
], k.prototype, "currentStepIndex", 2);
L([
  c({ type: Boolean, reflect: !0 })
], k.prototype, "loading", 2);
L([
  c({ type: String })
], k.prototype, "passkeyName", 2);
L([
  f()
], k.prototype, "activeConfig", 2);
L([
  f()
], k.prototype, "internalSteps", 2);
k = L([
  _("caas-passkey-confirm")
], k);
const Dt = `
  :root {
    --caas-color-primary: #ff4d00;
    --caas-color-primary-hover: color-mix(in srgb, var(--caas-color-primary) 85%, black);
    --caas-color-primary-active: color-mix(in srgb, var(--caas-color-primary) 70%, black);
    --caas-color-primary-alpha: color-mix(in srgb, var(--caas-color-primary) 10%, transparent);
    --caas-color-success: #16a34a;
    --caas-color-error: #ef4444;
    --caas-color-error-alpha: color-mix(in srgb, var(--caas-color-error) 10%, transparent);
    --caas-color-warning: #f59e0b;
    --caas-color-info: #3b82f6;
    --caas-text-primary: #f9fafb;
    --caas-text-secondary: #d1d5db;
    --caas-text-muted: #9ca3af;
    --caas-icon-color: #ffffff;
    --caas-card-background: #1f2937;
    --caas-card-border: #374151;
    --caas-card-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    --caas-card-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.4);
    --caas-id-background: #374151;
    --caas-id-border: #4b5563;
    --caas-button-hover-background: #374151;
    --caas-button-delete-background: #7f1d1d;
    --caas-button-delete-color: #dc2626;
    --caas-scrollbar-width: 8px;
    --caas-scrollbar-track: #374151;
    --caas-scrollbar-thumb: #6b7280;
    --caas-scrollbar-thumb-hover: #9ca3af;
    --caas-skeleton-start: #374151;
    --caas-skeleton-middle: #4b5563;
    --caas-status-success-bg: #064e3b;
    --caas-status-success-text: #a7f3d0;
    --caas-status-error-bg: #7f1d1d;
    --caas-status-error-text: #fca5a5;
    --caas-status-info-bg: #1e3a8a;
    --caas-status-info-text: #93c5fd;
    --caas-status-warning-bg: #78350f;
    --caas-status-warning-text: #fcd34d;
    --caas-spacing-1: 4px;
    --caas-spacing-2: 8px;
    --caas-spacing-3: 12px;
    --caas-spacing-4: 16px;
    --caas-spacing-5: 20px;
    --caas-spacing-6: 24px;
    --caas-spacing-8: 32px;
    --caas-spacing-12: 48px;
    --caas-spacing-16: 64px;
    --caas-font-size-xs: 11px;
    --caas-font-size-sm: 12px;
    --caas-font-size-base: 14px;
    --caas-font-size-lg: 16px;
    --caas-font-size-xl: 18px;
    --caas-font-size-2xl: 20px;
    --caas-font-weight-normal: 400;
    --caas-font-weight-medium: 500;
    --caas-font-weight-semibold: 600;
    --caas-line-height-tight: 1.2;
    --caas-line-height-normal: 1.5;
    --caas-radius-sm: 4px;
    --caas-radius-base: 8px;
    --caas-radius-lg: 12px;
    --caas-radius-xl: 16px;
    --caas-radius-full: 50%;
    --caas-transition-base: all 0.2s;
    --caas-dialog-bg: #1f2937;
    --caas-dialog-border-radius: 12px;
    --caas-dialog-border: 1px solid #374151;
    --caas-dialog-padding: 24px;
    --caas-dialog-max-width: 90vw;
    --caas-dialog-min-width: 455px;
    --caas-dialog-max-height: 90vh;
    --caas-dialog-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    --caas-dialog-shadow-2: 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    --caas-dialog-title-color: #f9fafb;
    --caas-dialog-subtitle-color: #d1d5db;
    --caas-dialog-body-color: #d1d5db;
    --caas-dialog-close-button-color: #9ca3af;
    --caas-dialog-close-button-bg-hover: #374151;
    --caas-dialog-close-button-color-hover: #f9fafb;
  }
`;
function jt() {
  if (typeof document < "u" && !document.getElementById("caas-theme")) {
    const t = document.createElement("style");
    t.id = "caas-theme", t.textContent = Dt;
    const e = document.head, s = e.firstChild;
    s ? e.insertBefore(t, s) : e.appendChild(t);
  }
}
export {
  x as CaaSLoginButton,
  E as CaaSPasskeyButton,
  U as CaaSPasskeyList,
  C as CaasButton,
  X as CaasCard,
  $ as CaasDialog,
  y as CaasToast,
  Ae as EventTargetElement,
  Pt as PASSKEY_FLOW_CONFIGS,
  k as PasskeyConfirm,
  g as PasskeyDialog,
  vt as ToastManager,
  Rt as createPasskeyFlowConfig,
  At as getPasskeyFlowConfig,
  jt as injectTheme,
  Ee as mainContext,
  W as toast
};
